import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ModuleHeader from "../components/shared/ModuleHeader";
import SavingNotice from "../components/shared/SavingNotice";
import CenteredModal from "../components/shared/CenteredModal";
import ConfirmModal from "../components/shared/ConfirmModal";
import { SheetLogoRow } from "../components/shared/SheetChrome";
import useMinLoad from "../hooks/useMinLoad";
import useFormPresentation from "../hooks/useFormPresentation";
import useBackButton from "../hooks/useBackButton";
import { utilitiesApi } from "../services/api";
import { useLocation } from "react-router-dom";

const IMG_UTILITIES = "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1400&q=75&auto=format&fit=crop";

const SERVICE_TYPES = [
  ["electric", "Electric"],
  ["gas", "Gas"],
  ["water", "Water"],
  ["internet", "Internet"],
  ["cable", "Cable / bundle"],
  ["phone", "Phone"],
  ["cell", "Cell plan"],
  ["solar", "Solar"],
  ["ev_charging", "EV charging"],
  ["trash", "Trash"],
  ["sewer", "Sewer"],
  ["hoa", "HOA"],
  ["lawn", "Lawn care"],
  ["pest", "Pest control"],
  ["cleaning", "Cleaning"],
  ["pool", "Pool service"],
  ["security", "Security"],
  ["snow", "Snow removal"],
  ["gutter", "Gutter cleaning"],
  ["window", "Window washing"],
  ["other", "Other"],
];

const PRIMARY_ADD_TYPES = ["electric", "gas", "water", "internet"];
const SECONDARY_ADD_TYPES = ["solar", "phone", "ev_charging", "trash"];
const MORE_ADD_TYPES = ["lawn", "pest", "hoa", "pool", "security", "cable", "sewer", "cleaning", "snow", "gutter", "window", "other"];
const TYPE_LABEL_OVERRIDES = {
  phone: "Phone/Cell",
  hoa: "HOA fee",
};
const FIXED_TYPES = new Set(["internet", "cable", "phone", "cell", "hoa", "lawn", "pest", "cleaning", "pool", "security", "snow", "gutter", "window"]);
const HOME_SERVICE_TYPES = new Set(["lawn", "pest", "cleaning", "pool", "snow", "gutter", "window", "security"]);
const SERVICE_ICONS = {
  electric: "⚡",
  gas: "🔥",
  water: "💧",
  internet: "📡",
  cable: "📺",
  phone: "📱",
  cell: "📱",
  solar: "☀️",
  ev_charging: "🔋",
  trash: "🗑",
  sewer: "🔧",
  hoa: "🏘",
  lawn: "🌿",
  pest: "•",
  cleaning: "✦",
  pool: "●",
  security: "🔒",
  snow: "❄",
  gutter: "⌂",
  window: "▣",
  other: "📋",
};

const BILLING_CYCLES = [
  ["", "Not set"],
  ["monthly", "Monthly"],
  ["quarterly", "Quarterly"],
  ["biyearly", "Every 6 months"],
  ["annual", "Annual"],
  ["per_visit", "Per visit"],
  ["other", "Other"],
];

const SERVICE_FREQUENCIES = [
  ["", "Not set"],
  ["weekly", "Weekly"],
  ["biweekly", "Every 2 weeks"],
  ["monthly", "Monthly"],
  ["quarterly", "Quarterly"],
  ["seasonal", "Seasonal"],
  ["annual", "Annual"],
  ["per_visit", "Per visit"],
];

const COMMON_PROVIDER_SUGGESTIONS = [
  "ComEd",
  "Con Edison",
  "Duke Energy",
  "National Grid",
  "PG&E",
  "PSEG",
  "Southern California Edison",
  "Xcel Energy",
  "Spectrum",
  "Xfinity",
  "AT&T",
  "Verizon",
  "T-Mobile",
  "Waste Management",
  "Republic Services",
  "Terminix",
  "Orkin",
  "ADT",
];

const UNIT_OPTIONS = ["kWh", "therms", "gal", "ccf", "gallons", "units", "visits"];
const AUTOPAY_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => String(index + 1));
const DEFAULT_UNITS = {
  electric: "kWh",
  solar: "kWh",
  ev_charging: "kWh",
  gas: "therms",
  water: "gal",
  sewer: "gal",
};

const UTILITIES_AI_SETUP_MESSAGE =
  "Utilities AI is not connected yet. Your bills still save normally; add an OpenAI or Anthropic API key on the backend to unlock bill explanations and reports.";

function isUtilitiesAiNotConfigured(err) {
  const code = String(err?.data?.error || err?.message || err || "").toLowerCase();
  return code.includes("ai_not_configured") || code.includes("ai not configured");
}

function utilitiesAiErrorMessage(err, fallback) {
  if (isUtilitiesAiNotConfigured(err)) {
    return err?.data?.message || UTILITIES_AI_SETUP_MESSAGE;
  }
  return err?.data?.message || err?.message || fallback;
}

function SavedReportRow({ label, report, expanded, onToggle, children }) {
  if (!report) return null;
  const generatedLabel = report.created_at
    ? `Generated ${fmtDate(report.created_at.slice(0, 10), "Saved")}`
    : "Saved report";
  return (
    <div className={`hm-sub-saved-report${report.needs_refresh ? " needs-refresh" : ""}`}>
      <button type="button" className="hm-sub-saved-report-toggle" onClick={onToggle} aria-expanded={expanded}>
        <span>{generatedLabel}</span>
        <strong>{label}</strong>
        <em aria-hidden="true">{expanded ? "⌃" : "⌄"}</em>
      </button>
      {expanded && <div className="hm-sub-saved-report-drawer">{children}</div>}
    </div>
  );
}

function ReplaceReportBanner({ onCancel, onReplace }) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
    };
  }, []);

  return (
    <div className="hm-sub-replace-modal-shell hm-util-replace-sheet-shell" role="dialog" aria-modal="true">
      <button type="button" className="hm-sub-replace-modal-scrim" onClick={onCancel} aria-label="Cancel replacement" />
      <div className="hm-sub-replace-modal">
        <div className="hm-util-replace-modal-top">
          <span aria-hidden="true" />
          <button type="button" onClick={onCancel} aria-label="Cancel replacement">×</button>
        </div>
        <div className="hm-util-replace-modal-head">
          <span aria-hidden="true">⌂</span>
          <strong>Replace report</strong>
        </div>
        <div className="hm-sub-replace-modal-body">
          <strong>Generate a new report?</strong>
          <p>The saved report below will be permanently replaced. This cannot be undone.</p>
          <div className="hm-sub-replace-modal-actions">
            <button type="button" onClick={onCancel}>Cancel</button>
            <button type="button" onClick={onReplace}>Replace report</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function copyUtilityText(value) {
  if (!value || typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;
  navigator.clipboard.writeText(String(value)).catch(() => {});
}

function UtilityCopyButton({ value, label = "Copy" }) {
  if (!value) return null;
  return (
    <button type="button" className="hm-util-copy-btn" onClick={() => copyUtilityText(value)}>
      {label}
    </button>
  );
}

function MobileCollapseSection({ storageKey, title, badge, defaultOpen = false, openSignal = 0, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved == null ? defaultOpen : saved === "true";
    } catch {
      return defaultOpen;
    }
  });

  const toggle = () => {
    setOpen(current => {
      const next = !current;
      try { localStorage.setItem(storageKey, String(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    if (!openSignal) return;
    setOpen(true);
    try { localStorage.setItem(storageKey, "true"); } catch {}
  }, [openSignal, storageKey]);

  return (
    <section className={`hm-mobile-collapse${open ? " is-open" : ""}`}>
      <button type="button" className="hm-mobile-collapse-toggle" onClick={toggle} aria-expanded={open}>
        <span>{title}</span>
        {badge && <em>{badge}</em>}
        <b>{open ? "⌃" : "⌄"}</b>
      </button>
      <div className="hm-mobile-collapse-body">{children}</div>
    </section>
  );
}

function UtilityReportShell({ tone = "mint", icon = "✦", title, eyebrow, badge, children, actions }) {
  return (
    <div className={`hm-util-ai-card is-${tone}`}>
      <div className="hm-util-ai-topbar">
        <span className="hm-util-ai-icon">{icon}</span>
        <div>
          {eyebrow && <small>{eyebrow}</small>}
          <strong>{title}</strong>
        </div>
        {badge && <em>{badge}</em>}
      </div>
      <div className="hm-util-ai-body">{children}</div>
      {actions && <div className="hm-util-ai-actions">{actions}</div>}
    </div>
  );
}

function UtilityReportSection({ title, children, badge }) {
  return (
    <section className="hm-util-ai-section">
      <div className="hm-util-ai-section-head">
        <strong>{title}</strong>
        {badge && <span>{badge}</span>}
      </div>
      {children}
    </section>
  );
}

function UtilityMiniBars({ values = [] }) {
  const clean = values.filter(item => Number(item.value ?? item.amount) > 0).slice(-6);
  if (!clean.length) return null;
  const max = Math.max(...clean.map(item => Number(item.value ?? item.amount) || 0), 1);
  return (
    <div className="hm-util-ai-bars">
      {clean.map((item, index) => {
        const value = Number(item.value ?? item.amount) || 0;
        return (
          <span key={`${item.label || index}-${index}`} className={item.tone === "spike" ? "is-current" : item.tone === "muted" ? "is-muted" : ""}>
            <em>{fmt$(value, 0)}</em>
            <b style={{ "--bar-h": `${Math.max(18, Math.round((value / max) * 100))}%` }} />
            <small>{item.label || `M${index + 1}`}</small>
          </span>
        );
      })}
    </div>
  );
}

function UtilityFallbackBarsFromBill(bill) {
  const entries = (bill?.recent_entries || []).slice().reverse().map((entry, index, all) => ({
    label: entry.bill_date ? fmtShortDate(entry.bill_date).split(" ")[0] : "",
    value: Number(entry.amount || 0),
    tone: index < Math.max(0, all.length - 2) ? "muted" : "",
  }));
  if (entries.length >= 2) return entries;
  const baseline = Number(bill?.history_avg_amount || bill?.previous_amount || bill?.avg_monthly_amount || bill?.amount || 0);
  const current = Number(bill?.amount || 0);
  const variance = baseline ? Math.abs(current - baseline) / baseline : 0;
  if (!baseline || !current || variance < 0.03) return [];
  return [
    { label: "Avg", value: baseline },
    { label: "Now", value: current, tone: current > baseline ? "spike" : "" },
  ];
}

function UtilitySpikeReportBody({ bill, result }) {
  if (!bill && !result) return null;
  const spike = Number(bill?.avg_spike_pct || bill?.spike_pct || 0);
  const fixes = result?.fixes || bill?.ai_fixes || [];
  const cause = result?.cause || bill?.ai_cause || "Review the latest bill against your normal pattern.";
  return (
    <UtilityReportShell
      tone="warn"
      icon={serviceGlyph(bill?.service_type)}
      title={bill?.provider || "Utility spike analysis"}
      eyebrow={`${serviceLabel(bill?.service_type)}${bill?.due_date ? ` · ${fmtShortDate(bill.due_date)}` : ""}`}
      badge={spike ? `+${Math.round(spike)}%` : "Spike"}
      actions={<button type="button" onClick={() => copyUtilityText(cause)}>Copy cause</button>}
    >
      <UtilityReportSection title="Bill history" badge={`${fmt$(bill?.amount, 0)} this bill`}>
        <UtilityMiniBars values={UtilityFallbackBarsFromBill(bill)} />
      </UtilityReportSection>
      <UtilityReportSection title="Most likely cause" badge={result?.confidence ? `${result.confidence}%` : "AI"}>
        <p>{cause}</p>
        <UtilityCopyButton value={cause} />
      </UtilityReportSection>
      {fixes.length > 0 && (
        <UtilityReportSection title="Action plan">
          <div className="hm-util-ai-fixes">
            {fixes.slice(0, 3).map((fix, index) => (
              <div key={`${fix.action || fix.title}-${index}`}>
                <span>{index + 1}</span>
                <strong>{fix.title || fix.action}</strong>
                {fix.savings && <em>{fix.savings}</em>}
                {fix.description && <p>{fix.description}</p>}
                <UtilityCopyButton value={fix.action || fix.title || fix.description} />
              </div>
            ))}
          </div>
        </UtilityReportSection>
      )}
      <UtilityReportSection title="Call provider?">
        <div className="hm-util-ai-callout">
          {result?.call_provider || bill?.ai_call_provider ? "Yes. Ask them to explain the bill change and verify the meter/rate." : "Not first. Try the action plan, then call if the next bill stays high."}
        </div>
      </UtilityReportSection>
    </UtilityReportShell>
  );
}

function UtilityRatePlanReportBody({ result, bill }) {
  if (!result) return null;
  const recommendations = result.recommendations || [];
  const annual = billAnnualAmount(bill);
  return (
    <UtilityReportShell
      tone="blue"
      icon="↯"
      title="Rate plan optimizer"
      eyebrow={bill?.provider || "Utility service"}
      badge={result.tou_opportunity ? "TOU opportunity" : "Plan review"}
      actions={result.action && <button type="button" onClick={() => copyUtilityText(result.action)}>Copy ask</button>}
    >
      <UtilityReportSection title="Current plan summary" badge={bill?.rate_plan || "Current"}>
        <p>{result.current_plan_assessment || "Review your current plan against usage and billing history."}</p>
      </UtilityReportSection>
      <UtilityReportSection title="Usage pattern">
        <UtilityMiniBars values={(bill?.recent_entries || []).slice().reverse().map(entry => ({ label: entry.bill_date ? fmtShortDate(entry.bill_date).split(" ")[0] : "", value: Number(entry.amount || 0) }))} />
      </UtilityReportSection>
      {recommendations.length > 0 && (
        <UtilityReportSection title="Plan comparison">
          <div className="hm-util-ai-table">
            <div><span>Current</span><strong>{bill?.rate_plan || "Standard"}</strong><em>{annual ? fmt$(annual, 0) : "Saved history"}</em></div>
            {recommendations.slice(0, 3).map((rec, index) => (
              <div key={`${rec.plan}-${index}`}>
                <span>{rec.best_for || "Alternative"}</span>
                <strong>{rec.plan}</strong>
                <em>{rec.estimated_savings || "Compare with provider"}</em>
              </div>
            ))}
          </div>
        </UtilityReportSection>
      )}
      <UtilityReportSection title="Recommended next step">
        <div className="hm-util-ai-callout">{result.action || "Ask your provider which plan best matches this usage pattern."}</div>
        <UtilityCopyButton value={result.action} />
      </UtilityReportSection>
      {(result.rebates?.length > 0 || result.budget_billing != null) && (
        <UtilityReportSection title="Caveats and programs">
          <div className="hm-util-ai-tags">
            {result.budget_billing && <span>Budget billing</span>}
            {result.tou_savings && <span>{result.tou_savings}</span>}
            {result.rebates?.slice(0, 3).map(item => <span key={item}>{item}</span>)}
          </div>
        </UtilityReportSection>
      )}
    </UtilityReportShell>
  );
}

function UtilityProviderReportBody({ result, bill }) {
  if (!result) return null;
  const checklist = result.comparison_checklist || [];
  const questions = result.provider_questions || [];
  return (
    <UtilityReportShell
      tone="mint"
      icon="⇄"
      title="Provider comparison"
      eyebrow={bill?.provider || "Current provider"}
      badge={result.switching_potential || "Unknown"}
      actions={result.action && <button type="button" onClick={() => copyUtilityText(result.action)}>Copy ask</button>}
    >
      <UtilityReportSection title="Current provider and market" badge={result.switching_potential || "Check market"}>
        <p>{result.market_assessment || "Compare your current provider, rate plan, and contract terms before switching."}</p>
      </UtilityReportSection>
      {checklist.length > 0 && (
        <UtilityReportSection title="What to compare">
          <div className="hm-util-ai-list">
            {checklist.slice(0, 4).map(item => <span key={item}>{item}</span>)}
          </div>
        </UtilityReportSection>
      )}
      {questions.length > 0 && (
        <UtilityReportSection title="Provider script">
          <div className="hm-util-ai-script">
            {questions.slice(0, 3).map(question => (
              <div key={question}><p>{question}</p><UtilityCopyButton value={question} /></div>
            ))}
          </div>
        </UtilityReportSection>
      )}
      {(result.estimated_savings || result.green_energy_note) && (
        <UtilityReportSection title="Savings and options">
          {result.estimated_savings && <div className="hm-util-ai-callout">Estimated savings: {result.estimated_savings}</div>}
          {result.green_energy_note && <p>{result.green_energy_note}</p>}
        </UtilityReportSection>
      )}
    </UtilityReportShell>
  );
}

function UtilityNegotiationReportBody({ result, bill }) {
  if (!result) return null;
  const leverage = result.key_leverage_points || [];
  const questions = result.questions_to_ask || [];
  const offers = result.offers_to_request || [];
  return (
    <UtilityReportShell
      tone="mint"
      icon="◎"
      title="Negotiation script"
      eyebrow={bill?.provider || "Provider"}
      badge={result.expected_savings || "Script"}
      actions={result.opening_statement && <button type="button" onClick={() => copyUtilityText(result.opening_statement)}>Copy opener</button>}
    >
      {result.opening_statement && (
        <UtilityReportSection title="How to open the call">
          <div className="hm-util-ai-script">
            <div><p>{result.opening_statement}</p><UtilityCopyButton value={result.opening_statement} /></div>
          </div>
        </UtilityReportSection>
      )}
      {leverage.length > 0 && (
        <UtilityReportSection title="Your leverage">
          <div className="hm-util-ai-list">
            {leverage.slice(0, 4).map(item => <span key={item}>{item}</span>)}
          </div>
        </UtilityReportSection>
      )}
      {questions.length > 0 && (
        <UtilityReportSection title="Questions to ask">
          <div className="hm-util-ai-script">
            {questions.slice(0, 3).map(q => (
              <div key={q}><p>{q}</p><UtilityCopyButton value={q} /></div>
            ))}
          </div>
        </UtilityReportSection>
      )}
      {offers.length > 0 && (
        <UtilityReportSection title="Offers to request">
          <div className="hm-util-ai-list">
            {offers.slice(0, 4).map(item => <span key={item}>{item}</span>)}
          </div>
        </UtilityReportSection>
      )}
      {(result.fallback_script || result.best_time_to_call) && (
        <UtilityReportSection title="If they say no">
          {result.fallback_script && <p>{result.fallback_script}</p>}
          {result.best_time_to_call && <div className="hm-util-ai-callout">Best time to call: {result.best_time_to_call}</div>}
        </UtilityReportSection>
      )}
    </UtilityReportShell>
  );
}

function UtilityServiceReviewBody({ result, bill, title = "Review" }) {
  if (!result) return null;
  const actions = result.action_items || [];
  const lowBasicCharge = Number(bill?.amount || bill?.avg_monthly_amount || 0) <= 30;
  const savingsBadge = lowBasicCharge && /\$/.test(String(result.estimated_savings || ""))
    ? "Review"
    : (result.estimated_savings || "Review");
  return (
    <UtilityReportShell
      tone="mint"
      icon={serviceGlyph(bill?.service_type)}
      title={title}
      eyebrow={bill?.provider}
      badge={savingsBadge}
    >
      {result.summary && (
        <UtilityReportSection title="Summary">
          <p>{result.summary}</p>
        </UtilityReportSection>
      )}
      {actions.length > 0 && (
        <UtilityReportSection title="Action items">
          <div className="hm-util-ai-list">
            {actions.slice(0, 4).map(item => <span key={item}>{item}</span>)}
          </div>
        </UtilityReportSection>
      )}
      {result.next_step && (
        <UtilityReportSection title="Next step">
          <div className="hm-util-ai-callout">{result.next_step}</div>
        </UtilityReportSection>
      )}
    </UtilityReportShell>
  );
}

function UtilitySolarLoanReportBody({ result, bill }) {
  if (!result) return null;
  const comparison = result.savings_comparison || {};
  const missingInputs = comparison.missing_inputs || [];
  const trustedSavings = (
    comparison.estimated_monthly_net_savings != null
    && comparison.months_sampled >= 6
    && !missingInputs.includes("pre-solar or grid-only electric baseline")
  );
  return (
    <UtilityReportShell tone="mint" icon="☀️" title="Loan payoff" eyebrow={bill?.provider} badge="Solar loan">
      {result.summary && <UtilityReportSection title="Summary"><p>{result.summary}</p></UtilityReportSection>}
      {result.estimated_payoff && (
        <UtilityReportSection title="Estimated payoff">
          <div className="hm-util-ai-callout">{result.estimated_payoff}</div>
        </UtilityReportSection>
      )}
      {(result.savings_vs_grid || comparison.estimated_monthly_net_savings != null || comparison.missing_inputs?.length > 0) && (
        <UtilityReportSection title="Solar vs grid">
          <div className="hm-util-ai-card-grid">
            <div><span>Grid avg</span><strong>{comparison.avg_grid_charge_with_solar ? `${fmt$(comparison.avg_grid_charge_with_solar, 0)}/mo` : "Unknown"}</strong></div>
            <div><span>With solar</span><strong>{comparison.estimated_with_solar_monthly ? `${fmt$(comparison.estimated_with_solar_monthly, 0)}/mo` : "Unknown"}</strong></div>
            <div><span>Grid-only est.</span><strong>{trustedSavings && comparison.estimated_grid_only_monthly ? `${fmt$(comparison.estimated_grid_only_monthly, 0)}/mo` : "Need baseline"}</strong></div>
            <div><span>Net</span><strong>{trustedSavings ? `${fmt$(comparison.estimated_monthly_net_savings, 0)}/mo` : "Unknown"}</strong></div>
          </div>
          {trustedSavings && result.savings_vs_grid && <p>{result.savings_vs_grid}</p>}
          {comparison.months_sampled > 0 && (
            <div className="hm-util-ai-callout">
              Based on {comparison.months_sampled} electric bill month{comparison.months_sampled === 1 ? "" : "s"} averaging {comparison.avg_grid_usage_kwh || "unknown"} kWh/mo.
            </div>
          )}
          {!trustedSavings && (
            <div className="hm-util-ai-callout">
              Solar savings need a pre-solar or grid-only baseline. Current grid/basic charges are not counted as savings.
            </div>
          )}
          {missingInputs.length > 0 && (
            <div className="hm-util-ai-list">
              {missingInputs.slice(0, 4).map(item => <span key={item}>Missing: {item}</span>)}
            </div>
          )}
        </UtilityReportSection>
      )}
      {result.break_even_estimate && (
        <UtilityReportSection title="Break-even">
          <div className="hm-util-ai-callout">{result.break_even_estimate}</div>
        </UtilityReportSection>
      )}
      {(result.action_items || []).length > 0 && (
        <UtilityReportSection title="Next steps">
          <div className="hm-util-ai-list">{(result.action_items || []).map(a => <span key={a}>{a}</span>)}</div>
        </UtilityReportSection>
      )}
    </UtilityReportShell>
  );
}

function UtilitySolarSavingsReportBody({ result, bill }) {
  if (!result) return null;
  return (
    <UtilityReportShell tone="mint" icon="☀️" title="Savings check" eyebrow={bill?.provider} badge={result.savings_verdict || "Solar"}>
      {result.summary && <UtilityReportSection title="Summary"><p>{result.summary}</p></UtilityReportSection>}
      {(result.tips_to_maximize_savings || []).length > 0 && (
        <UtilityReportSection title="Maximize savings">
          <div className="hm-util-ai-list">{(result.tips_to_maximize_savings || []).map(t => <span key={t}>{t}</span>)}</div>
        </UtilityReportSection>
      )}
      {result.next_step && <UtilityReportSection title="Next step"><div className="hm-util-ai-callout">{result.next_step}</div></UtilityReportSection>}
    </UtilityReportShell>
  );
}

function UtilitySolarProductionReportBody({ result, bill }) {
  if (!result) return null;
  return (
    <UtilityReportShell tone="mint" icon="☀️" title="Production review" eyebrow={bill?.provider} badge={result.production_verdict || "Solar"}>
      {result.summary && <UtilityReportSection title="Summary"><p>{result.summary}</p></UtilityReportSection>}
      {(result.factors_affecting_production || []).length > 0 && (
        <UtilityReportSection title="Factors">
          <div className="hm-util-ai-list">{(result.factors_affecting_production || []).map(f => <span key={f}>{f}</span>)}</div>
        </UtilityReportSection>
      )}
      {result.next_step && <UtilityReportSection title="Next step"><div className="hm-util-ai-callout">{result.next_step}</div></UtilityReportSection>}
    </UtilityReportShell>
  );
}

function UtilitySolarRateReportBody({ result, bill }) {
  if (!result) return null;
  return (
    <UtilityReportShell tone="mint" icon="☀️" title="Rate comparison" eyebrow={bill?.provider} badge={result.rate_verdict || "Solar"}>
      {result.summary && <UtilityReportSection title="Summary"><p>{result.summary}</p></UtilityReportSection>}
      {(result.questions_to_ask_provider || []).length > 0 && (
        <UtilityReportSection title="Questions to ask">
          <div className="hm-util-ai-list">{(result.questions_to_ask_provider || []).map(q => <span key={q}>{q}</span>)}</div>
        </UtilityReportSection>
      )}
      {result.next_step && <UtilityReportSection title="Next step"><div className="hm-util-ai-callout">{result.next_step}</div></UtilityReportSection>}
    </UtilityReportShell>
  );
}

function UtilitySolarCreditsReportBody({ result, bill }) {
  if (!result) return null;
  return (
    <UtilityReportShell tone="mint" icon="☀️" title="Credits review" eyebrow={bill?.provider} badge={result.credits_verdict || "Solar"}>
      {result.summary && <UtilityReportSection title="Summary"><p>{result.summary}</p></UtilityReportSection>}
      {(result.tips_to_maximize_credits || []).length > 0 && (
        <UtilityReportSection title="Maximize credits">
          <div className="hm-util-ai-list">{(result.tips_to_maximize_credits || []).map(t => <span key={t}>{t}</span>)}</div>
        </UtilityReportSection>
      )}
      {result.next_step && <UtilityReportSection title="Next step"><div className="hm-util-ai-callout">{result.next_step}</div></UtilityReportSection>}
    </UtilityReportShell>
  );
}

function UtilityEfficiencyReportBody({ result }) {
  if (!result) return null;
  const quickWins = result.quick_wins || [];
  const investments = result.investments || [];
  return (
    <UtilityReportShell
      tone="mint"
      icon="⌁"
      title="Annual efficiency report"
      eyebrow="Workspace-level"
      badge={result.grade ? `Grade ${result.grade}` : "Efficiency"}
    >
      <UtilityReportSection title="Efficiency grade" badge={result.total_savings_potential}>
        <div className="hm-util-ai-grade">
          <strong className={`grade-${result.grade?.toLowerCase()}`}>{result.grade || "?"}</strong>
          <p>{result.grade_explanation || result.summary}</p>
        </div>
      </UtilityReportSection>
      {result.summary && (
        <UtilityReportSection title="Summary">
          <p>{result.summary}</p>
        </UtilityReportSection>
      )}
      {quickWins.length > 0 && (
        <UtilityReportSection title="Quick wins">
          <div className="hm-util-ai-fixes">
            {quickWins.slice(0, 3).map((item, index) => (
              <div key={`${item.action}-${index}`}>
                <span>{index + 1}</span>
                <strong>{item.action}</strong>
                {item.monthly_savings && <em>{item.monthly_savings}/mo</em>}
                <p>{[item.cost, item.payback].filter(Boolean).join(" · ")}</p>
                <UtilityCopyButton value={item.action} />
              </div>
            ))}
          </div>
        </UtilityReportSection>
      )}
      {investments.length > 0 && (
        <UtilityReportSection title="Long-term investments">
          <div className="hm-util-ai-table">
            {investments.slice(0, 3).map((item, index) => (
              <div key={`${item.action}-${index}`}>
                <span>{item.payback || "Payback"}</span>
                <strong>{item.action}</strong>
                <em>{item.annual_savings || item.cost}</em>
              </div>
            ))}
          </div>
        </UtilityReportSection>
      )}
      {result.priority_action && (
        <UtilityReportSection title="Priority action">
          <div className="hm-util-ai-callout">{result.priority_action}</div>
          <UtilityCopyButton value={result.priority_action} />
        </UtilityReportSection>
      )}
    </UtilityReportShell>
  );
}

const BLANK_BILL = {
  service_type: "electric",
  provider: "",
  account_number: "",
  service_address: "",
  billing_cycle: "monthly",
  avg_monthly_amount: "",
  autopay_date: "",
  contract_end_date: "",
  rate_plan: "",
  service_frequency: "",
  last_service_date: "",
  next_service_date: "",
  cost_per_visit: "",
  amount: "",
  previous_amount: "",
  due_date: "",
  billing_period_start: "",
  billing_period_end: "",
  usage_amount: "",
  usage_unit: "",
  status: "active",
  autopay: false,
  solar_system_cost: "",
  solar_monthly_loan_payment: "",
  solar_loan_balance: "",
  solar_apr: "",
  solar_loan_term_months: "",
  solar_loan_start_date: "",
  solar_estimated_monthly_production: "",
  solar_net_metering: false,
  notes: "",
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysISO(dateValue, days) {
  const base = dateValue ? new Date(`${dateValue}T00:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
}

function addMonthsToISO(isoDate, months) {
  const base = isoDate ? new Date(`${isoDate}T12:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  base.setMonth(base.getMonth() + months);
  return base.toISOString().slice(0, 10);
}

function cycleMonths(billingCycle) {
  const cycle = (billingCycle || "monthly").toLowerCase();
  if (cycle === "quarterly") return 3;
  if (cycle === "biyearly") return 6;
  if (cycle === "annual" || cycle === "yearly") return 12;
  return 1;
}

function fmt$(value, decimals = 2) {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  const effectiveDecimals = decimals === 2 && Number.isInteger(num) ? 0 : decimals;
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: effectiveDecimals,
    maximumFractionDigits: effectiveDecimals,
  });
}

function fmtOptional$(value) {
  if (value == null || value === "") return "—";
  return fmt$(value);
}

function fmtCompact$(value) {
  return fmt$(value || 0, 0);
}

function fmtDate(iso, fallback = "No due date") {
  if (!iso) return fallback;
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtShortDate(iso) {
  if (!iso) return "-";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtBillMonth(iso) {
  if (!iso) return "-";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "2-digit" }).replace(" ", " '");
}

function daysUntil(iso) {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(`${iso}T00:00:00`) - today) / 86400000);
}

function utilityEdgeClass(bill, dueDays, spikePct, contractDays) {
  if (bill.status === "paid") return "is-ready";
  if (["cancelled", "inactive", "paused"].includes(bill.status)) return "is-past";
  if (dueDays != null && dueDays <= 5) return "is-urgent";
  if (dueDays != null && dueDays <= 14) return "is-soon";
  if (spikePct >= 20 || (contractDays != null && contractDays >= 0 && contractDays <= 45)) return "is-soon";
  if (bill.autopay) return "is-ready";
  return "is-calm";
}

function serviceLabel(type) {
  return SERVICE_TYPES.find(([key]) => key === type)?.[1] || "Utility";
}

function serviceTileLabel(type, label) {
  return TYPE_LABEL_OVERRIDES[type] || label;
}

function serviceGlyph(type) {
  return SERVICE_ICONS[type] || "📋";
}

function cycleMonthlyAmount(amount, billingCycle, serviceFrequency) {
  const value = Number(amount || 0);
  const cycle = String(billingCycle || serviceFrequency || "monthly").toLowerCase();
  if (!value) return 0;
  if (cycle === "quarterly") return value / 3;
  if (cycle === "biyearly") return value / 6;
  if (cycle === "annual") return value / 12;
  if (cycle === "weekly") return value * 52 / 12;
  if (cycle === "biweekly") return value * 26 / 12;
  if (cycle === "seasonal") return value * 2 / 12;
  if (cycle === "per_visit" || cycle === "other") return 0;
  return value;
}

function billMonthlyAmount(bill) {
  if (!bill) return 0;
  if (HOME_SERVICE_TYPES.has(bill.service_type)) {
    const amount = bill.cost_per_visit ?? bill.avg_monthly_amount ?? bill.amount;
    return cycleMonthlyAmount(amount, bill.billing_cycle, bill.service_frequency);
  }
  return cycleMonthlyAmount(bill.avg_monthly_amount ?? bill.amount, bill.billing_cycle);
}

function billAnnualAmount(bill) {
  return billMonthlyAmount(bill) * 12;
}

function isSavedEfficiencyReportCurrent(report, summary) {
  if (!report || report.needs_refresh) return false;
  const sourceMonthly = Number(report.source_context_json?.monthly_total || 0);
  const currentMonthly = Number(summary?.monthly_total || 0);
  if (!sourceMonthly && currentMonthly) return false;
  if (!sourceMonthly || !currentMonthly) return true;
  return Math.abs(sourceMonthly - currentMonthly) <= Math.max(5, currentMonthly * 0.05);
}

function billCycleAmount(bill) {
  if (!bill) return 0;
  return Number((HOME_SERVICE_TYPES.has(bill.service_type) ? bill.cost_per_visit : null) ?? bill.amount ?? bill.avg_monthly_amount ?? 0);
}

function cycleDisplayLabel(bill) {
  const cycle = bill?.service_frequency || bill?.billing_cycle || "monthly";
  const labels = {
    monthly: "monthly",
    quarterly: "quarterly",
    biyearly: "every 6 months",
    annual: "annual",
    weekly: "weekly",
    biweekly: "every 2 weeks",
    seasonal: "seasonal",
    per_visit: "per visit",
    other: "per bill",
  };
  return labels[cycle] || "per bill";
}

function utilityActionConfig(bill) {
  const type = bill?.service_type;
  const spike = Number(bill?.avg_spike_pct || bill?.spike_pct || 0);
  const hasSpikeAction = spike >= 20 || Boolean(bill?.ai_cause || bill?.ai_summary);
  const spikeAction = hasSpikeAction
    ? [{
        label: "Analyze spike",
        report: "spike_analysis",
        prompt: `Analyze my ${serviceLabel(type).toLowerCase()} spike for ${bill.provider}.`,
      }]
    : [];
  const rateTypes = new Set(["electric", "ev_charging"]);
  const providerTypes = new Set(["internet", "cable", "phone", "cell"]);
  const serviceOnlyTypes = new Set(["trash", "hoa", "lawn", "pest", "cleaning", "pool", "snow", "gutter", "window", "security"]);
  const meteredEfficiencyTypes = new Set(["gas", "water", "sewer"]);
  if (serviceOnlyTypes.has(type)) {
    return {
      actions: [],
      secondary: null,
      tertiary: null,
    };
  }
  if (rateTypes.has(type)) {
    const rateAction = {
      label: "Rate plans",
      report: "rate_plan",
      prompt: `Compare rate plan options for ${bill.provider} using my saved ${serviceLabel(type).toLowerCase()} bills.`,
    };
    const detailAction = {
      label: type === "ev_charging" ? "EV charging" : "Efficiency",
      report: type === "ev_charging" ? "rate_plan" : "efficiency_tips",
      prompt: type === "ev_charging"
        ? `Review EV charging cost and overnight charging options for ${bill.provider}.`
        : `Review efficiency fixes for my ${serviceLabel(type).toLowerCase()} bill from ${bill.provider}.`,
    };
    return {
      actions: [...spikeAction, rateAction, detailAction],
      secondary: "Rate plans",
      tertiary: type === "ev_charging" ? "EV charging" : "Efficiency",
      secondaryReport: "rate_plan",
      tertiaryReport: type === "ev_charging" ? "rate_plan" : "efficiency_tips",
      secondaryPrompt: `Compare rate plan options for ${bill.provider} using my saved ${serviceLabel(type).toLowerCase()} bills.`,
      tertiaryPrompt: type === "ev_charging"
        ? `Review EV charging cost and overnight charging options for ${bill.provider}.`
        : `Review efficiency fixes for my ${serviceLabel(type).toLowerCase()} bill from ${bill.provider}.`,
    };
  }
  if (type === "solar") {
    const solarBillingType = bill.solar_billing_type;
    if (solarBillingType === "loan" || solarBillingType === "loan_net_metering") {
      const actions = [];
      if (hasSolarLoanInputs(bill)) {
        actions.push({
          label: "Loan payoff",
          report: "solar_loan_payoff",
          prompt: `Analyze payoff timing, break-even outlook, and next steps for my solar loan with ${bill.provider}.`,
        });
      }
      if (hasSolarSavingsInputs(bill)) {
        actions.push({
          label: "Savings check",
          report: "solar_savings_check",
          prompt: `Check whether my owned solar panels from ${bill.provider} are saving money versus grid power.`,
        });
      }
      if (!actions.length) {
        return {
          actions: [],
          secondary: null,
          tertiary: null,
          needsSolarFinance: true,
          solarFinancePrompt: "Add solar loan payment, balance, APR, term, and production or system cost before running solar AI.",
        };
      }
      return {
        actions,
        secondary: actions[0]?.label || null,
        secondaryReport: actions[0]?.report || null,
        secondaryPrompt: actions[0]?.prompt || "",
        tertiary: actions[1]?.label || null,
        tertiaryReport: actions[1]?.report || null,
        tertiaryPrompt: actions[1]?.prompt || "",
      };
    }
    if (solarBillingType === "lease_ppa") {
      return {
        actions: [
          { label: "Production", report: "solar_production_review", prompt: `Review production and value for my leased solar or PPA service from ${bill.provider}.` },
          { label: "Rate compare", report: "solar_rate_comparison", prompt: `Compare my solar lease or PPA rate from ${bill.provider} against grid costs and alternatives.` },
        ],
        secondary: "Production", secondaryReport: "solar_production_review", secondaryPrompt: "",
        tertiary: "Rate compare", tertiaryReport: "solar_rate_comparison", tertiaryPrompt: "",
      };
    }
    if (solarBillingType === "net_metering") {
      return {
        actions: [
          { label: "Credits review", report: "solar_credits_review", prompt: `Review my net metering credits and export value for ${bill.provider}.` },
          { label: "Production", report: "solar_production_review", prompt: `Review solar production and grid offset for ${bill.provider}.` },
        ],
        secondary: "Credits review", secondaryReport: "solar_credits_review", secondaryPrompt: "",
        tertiary: "Production", tertiaryReport: "solar_production_review", tertiaryPrompt: "",
      };
    }
    // No billing type set yet — prompt user to set it
    return { actions: [], secondary: null, tertiary: null, needsSolarType: true };
  }
  if (meteredEfficiencyTypes.has(type)) {
    const usageAction = {
      label: type === "solar" ? "Production" : "Usage review",
      report: "usage_review",
      prompt: `Review usage patterns for ${bill.provider}.`,
    };
    const efficiencyAction = {
      label: "Efficiency",
      report: "efficiency_tips",
      prompt: `Review efficiency fixes for my ${serviceLabel(type).toLowerCase()} service from ${bill.provider}.`,
    };
    return {
      actions: [...spikeAction, usageAction, efficiencyAction],
      secondary: type === "solar" ? "Production" : "Usage review",
      tertiary: "Efficiency",
      secondaryReport: "usage_review",
      tertiaryReport: "efficiency_tips",
      secondaryPrompt: `Review usage patterns for ${bill.provider}.`,
      tertiaryPrompt: `Review efficiency fixes for my ${serviceLabel(type).toLowerCase()} service from ${bill.provider}.`,
    };
  }
  if (providerTypes.has(type)) {
    const compareAction = {
      label: "Compare",
      report: "provider_comparison",
      prompt: `Compare provider or plan options for ${bill.provider}.`,
    };
    const negotiateAction = {
      label: "Negotiate",
      report: "negotiation",
      prompt: `Draft a negotiation script for ${bill.provider} based on my service cost and contract details.`,
    };
    return {
      actions: [...spikeAction, compareAction, negotiateAction],
      secondary: "Compare",
      tertiary: "Negotiate",
      secondaryReport: "provider_comparison",
      tertiaryReport: "negotiation",
      secondaryPrompt: `Compare provider or plan options for ${bill.provider}.`,
      tertiaryPrompt: `Draft a negotiation script for ${bill.provider} based on my service cost and contract details.`,
    };
  }
  const reviewAction = {
    label: "Review",
    report: "service_review",
    prompt: `Review this utility service and suggest the next best action.`,
  };
  return {
    actions: [reviewAction],
    secondary: "Review",
    tertiary: "Review",
    secondaryReport: "service_review",
    tertiaryReport: "service_review",
    secondaryPrompt: `Review this utility service and suggest the next best action.`,
    tertiaryPrompt: `Review this utility service and suggest the next best action.`,
  };
}

function supportsRatePlanField(bill) {
  return bill?.service_type === "electric";
}

function supportsMeterRate(bill) {
  return ["electric", "gas", "water", "sewer", "solar", "ev_charging"].includes(bill?.service_type);
}

function utilityAmountFieldLabel(bill) {
  if (bill?.service_type === "electric" || bill?.service_type === "ev_charging") return "Grid / utility monthly charge";
  if (bill?.service_type === "solar") return "Utility/minimum monthly charge";
  return "Typical amount";
}

function hasSolarLoanInputs(bill) {
  return Boolean(
    bill?.solar_loan_balance &&
    bill?.solar_monthly_loan_payment &&
    bill?.solar_apr &&
    (bill?.solar_loan_term_months || bill?.contract_end_date)
  );
}

function hasSolarSavingsInputs(bill) {
  return Boolean(
    bill?.solar_system_cost &&
    bill?.solar_monthly_loan_payment &&
    (bill?.solar_estimated_monthly_production || bill?.usage_amount)
  );
}

function isAiOptimizable(bill) {
  // Services where AI rate/provider/usage reports are meaningful
  return new Set([
    "electric", "gas", "water", "internet", "cable",
    "phone", "cell", "solar", "ev_charging", "sewer",
  ]).has(bill?.service_type);
}

function utilityInsightModuleLabel(type) {
  if (["hvac_efficiency", "water_heater_efficiency", "solar_efficiency"].includes(type)) return "Home Vault";
  if (type === "ev_charging") return "Vehicles";
  if (["cable_streaming_overlap", "internet_subscription_overlap"].includes(type)) return "Subscriptions";
  if (["trip_thermostat", "home_service_trip"].includes(type)) return "Trips";
  return "HouseMind";
}

function utilityInsightTitle(insight) {
  const type = insight?.insight_type || "";
  const tripName = insight?.trip?.name || "";
  if (type === "hvac_efficiency") return "HVAC x electric spike";
  if (type === "water_heater_efficiency") return "Water heater x water bill";
  if (type === "solar_efficiency") return "Solar x electric bill";
  if (type === "cable_streaming_overlap") return "Cable x streaming overlap";
  if (type === "internet_subscription_overlap") return "Internet tracked twice";
  if (type === "ev_charging") return "EV x rate plan";
  if (type === "trip_thermostat") return tripName ? `${tripName} utility prep` : "Travel utility prep";
  if (type === "home_service_trip") return tripName ? `${tripName} service check` : "Service x trip";
  return "Cross-module insight";
}

function utilityTripInsightMessage(message) {
  return String(message || "")
    .replace(/\bNone is coming up\./g, "Your trip is coming up.")
    .replace(/\bduring None\b/g, "during your trip")
    .replace(/\bfor None\b/g, "for your trip")
    .replace(/\bbefore None\b/g, "before your trip");
}

function utilityInsightMessage(insight) {
  return utilityTripInsightMessage(insight?.message || "");
}

function utilityCrossModuleKey(type, message) {
  return `${type || ""}::${utilityTripInsightMessage(message || "").trim().toLowerCase()}`;
}

const CROSS_MODULE_EMPTY_ROWS = [
  {
    icon: "HV",
    title: "Home Vault x utilities",
    message: "Add HVAC, water heater, or solar records to connect bill changes with home equipment.",
    label: "Learning",
  },
  {
    icon: "EV",
    title: "Vehicles x rate plans",
    message: "An EV in Vehicles can unlock overnight charging and time-of-use rate checks.",
    label: "Learning",
  },
  {
    icon: "TV",
    title: "Subscriptions x cable/internet",
    message: "Track internet, cable, and streaming services to spot duplicate tracking or overlap.",
    label: "Learning",
  },
];

function defaultUnitForService(type) {
  return DEFAULT_UNITS[type] || "";
}

function maskAccount(value) {
  if (!value) return "-";
  const text = String(value).trim();
  return text.length > 4 ? `****${text.slice(-4)}` : text;
}

function isFixedBill(bill) {
  return bill?.recent_entries?.[0]?.payment_type === "fixed" || (FIXED_TYPES.has(bill?.service_type) && !bill?.usage_amount);
}

function entryToForm(entry = {}, bill = {}) {
  return {
    bill_date: entry.bill_date || new Date().toISOString().slice(0, 10),
    amount: entry.amount ?? "",
    payment_type: entry.payment_type || (isFixedBill(bill) ? "fixed" : "usage"),
    due_date: entry.due_date || bill.due_date || "",
    amount_paid: entry.amount_paid ?? "",
    payment_date: entry.payment_date || "",
    payment_method: entry.payment_method || "",
    usage_amount: entry.usage_amount ?? "",
    usage_unit: entry.usage_unit || bill.usage_unit || defaultUnitForService(bill.service_type),
    status: entry.status || "active",
    notes: entry.notes || "",
  };
}

function BillsSheet({ bill, entries, entriesLoading, onClose, onSaved, onSavedDueDate }) {
  const [tab, setTab] = useState("log");
  const [removingEntryId, setRemovingEntryId] = useState(null);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  useUtilitySheetScrollLock(true);

  // Quick-log form state (mirrors QuickLogSheet but inline here)
  const monthly = isMonthly(bill.billing_cycle);
  const fixedBill = isFixedBill(bill);
  const fixedDefaultAmount = bill?.avg_monthly_amount || bill?.amount || "";
  const [amount, setAmount] = useState(() => fixedBill && fixedDefaultAmount ? String(fixedDefaultAmount) : "");
  const [usageAmount, setUsageAmount] = useState("");
  const [usageUnit, setUsageUnit] = useState(() => bill.usage_unit || defaultUnitForService(bill.service_type));
  const [billDate, setBillDate] = useState(() => defaultBillDate(bill.billing_cycle));
  const [markPaid, setMarkPaid] = useState(true);
  const [logBusy, setLogBusy] = useState(false);
  const [logError, setLogError] = useState("");

  const autoNextDue = addMonthsToISO(
    bill.due_date || new Date().toISOString().slice(0, 10),
    cycleMonths(bill.billing_cycle)
  );
  const [nextDueDate, setNextDueDate] = useState(autoNextDue);
  const lastEntry = (bill?.recent_entries || [])[0];
  const avg = Number(bill?.avg_monthly_amount || bill?.amount || 0);
  const historyAvg = billHistoryAverage(bill?.recent_entries || [], avg);

  function resolvedBillDate() {
    return monthly ? `${billDate}-01` : billDate;
  }

  async function handleLogSubmit(event) {
    event.preventDefault();
    if (!amount) { setLogError("Enter the bill amount."); return; }
    setLogBusy(true);
    setLogError("");
    try {
      const result = await utilitiesApi.addEntry(bill.id, {
        amount: Number(amount),
        bill_date: resolvedBillDate(),
        due_date: resolvedBillDate(),
        status: markPaid ? "paid" : "active",
        payment_type: fixedBill ? "fixed" : "usage",
        usage_amount: fixedBill ? null : toNumberOrNull(usageAmount),
        usage_unit: fixedBill ? "" : usageUnit,
      });
      onSaved(result, nextDueDate);
      // Reset form and switch to history to confirm
      setAmount("");
      setUsageAmount("");
      setBillDate(defaultBillDate(bill.billing_cycle));
      setMarkPaid(true);
      setTab("history");
    } catch (err) {
      setLogError(err.message || "Could not save bill.");
      setLogBusy(false);
    }
  }

  return (
    <div
      className="hm-gifts-sheet-overlay fd-mobile-sheet-backdrop hm-util-details-sheet-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Bills · ${bill.provider}`}
      onClick={onClose}
    >
      <button type="button" className="fd-mobile-sheet-scrim" onClick={e => { e.stopPropagation(); onClose(); }} aria-label="Close" />
      <section
        className="hm-gifts-action-sheet fd-mobile-sheet hm-util-details-sheet hm-util-bills-sheet"
        onClick={e => e.stopPropagation()}
      >
        <UtilitySheetTop onClose={onClose} closeLabel="Close bills" />
        <SheetLogoRow title={`Bills · ${bill.provider}`} onClose={onClose} closeLabel="Close" showClose={false} />
        <div className="hm-util-bills-sheet-tabs">
          <button type="button" className={tab === "log" ? "is-active" : ""} onClick={() => setTab("log")}>Log bill</button>
          <button type="button" className={tab === "history" ? "is-active" : ""} onClick={() => setTab("history")}>History</button>
        </div>
        <div className="hm-sheet-body hm-util-bills-sheet-body">
          {tab === "log" ? (
            <form className="hm-util-quick-log-form" onSubmit={handleLogSubmit}>
              {logError && <div className="hm-inline-error">{logError}</div>}
              {!monthly && (
                <div className="hm-util-quick-log-cycle-badge">
                  {bill.billing_cycle} bill · next due auto-advances {cycleMonths(bill.billing_cycle)} month{cycleMonths(bill.billing_cycle) > 1 ? "s" : ""}
                </div>
              )}
              <div className={`hm-util-live-bill-note hm-util-log-mode-note${fixedBill ? " is-good" : ""}`}>
                {fixedBill
                  ? "Fixed bill: HouseMind prefilled the regular amount. Confirm it, then save or let auto-log handle future cycles."
                  : "Changing bill: enter the current amount from this statement. Usage is optional but improves trends and rate checks."}
              </div>
              <label className="hm-util-quick-log-amount-label">
                <span>{fixedBill ? "Confirmed amount *" : "Current bill amount *"}</span>
                <input
                  className="hm-auth-input hm-util-quick-log-amount-input"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={avg ? `${fixedBill ? "Fixed" : "Usual"} $${avg.toFixed(0)}` : "$0.00"}
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  autoFocus
                />
              </label>
              {lastEntry && (
                <p className="hm-util-last-bill-hint">
                  Last logged: <strong>${Number(lastEntry.amount || 0).toFixed(0)}</strong>
                  {lastEntry.bill_date ? ` · ${fmtBillMonth(lastEntry.bill_date)}` : ""}
                  {!fixedBill && historyAvg ? ` · usual ${fmt$(historyAvg, 0)}` : ""}
                </p>
              )}
              <label className="hm-util-quick-log-month-label">
                <span>{billDateLabel(bill.billing_cycle)}</span>
                <input
                  className="hm-auth-input"
                  type={monthly ? "month" : "date"}
                  value={billDate}
                  onChange={e => setBillDate(e.target.value)}
                />
              </label>
              <label className="hm-util-quick-log-month-label">
                <span>{fixedBill ? "Due date" : "Next due date"} <em className="hm-util-quick-log-auto-hint">(editable)</em></span>
                <input
                  className="hm-auth-input"
                  type="date"
                  value={nextDueDate}
                  onChange={e => setNextDueDate(e.target.value)}
                />
              </label>
              {!fixedBill && (
                <div className="hm-util-live-bill-note">
                  Enter the new amount from this bill. HouseMind updates trends, spike checks, and reminders instantly.
                </div>
              )}
              {!fixedBill && (
                <div className="hm-util-date-pair hm-util-usage-pair">
                  <label>
                    <span>Usage optional</span>
                    <input
                      className="hm-auth-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={usageAmount}
                      onChange={e => setUsageAmount(e.target.value)}
                      placeholder={lastEntry?.usage_amount ? String(lastEntry.usage_amount) : "Usage"}
                    />
                  </label>
                  <label>
                    <span>Unit</span>
                    <select className="hm-auth-input" value={usageUnit || ""} onChange={e => setUsageUnit(e.target.value)}>
                      <option value="">Unit</option>
                      {UNIT_OPTIONS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                    </select>
                  </label>
                </div>
              )}
              <label className="hm-util-fixed-row">
                <input type="checkbox" checked={markPaid} onChange={e => setMarkPaid(e.target.checked)} />
                <span>Mark as paid</span>
              </label>
              <button type="submit" className="hm-btn hm-btn-primary hm-util-quick-log-save" disabled={logBusy}>
                {logBusy ? "Saving…" : "Save bill"}
              </button>
            </form>
          ) : (
            <div className="hm-util-ledger">
              {entriesLoading ? (
                <div className="hm-sub-loading">Loading history...</div>
              ) : (
                <>
                  <HistoryTrendChart entries={entries} />
                  <HistoryTable
                    bill={bill}
                    entries={entries}
                    editingId={editingEntryId}
                    removingId={removingEntryId}
                    onEdit={entry => setEditingEntryId(entry.id)}
                    onMarkPaid={async entry => {
                      try {
                        const result = await utilitiesApi.markEntryPaid(bill.id, entry.id);
                        onSaved(result, null);
                      } catch (_) {}
                    }}
                    onRemove={entry => setConfirmRemove(entry)}
                    onSaved={result => { onSaved(result, null); setEditingEntryId(null); }}
                    onCancelEdit={() => setEditingEntryId(null)}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </section>
      {confirmRemove && (
        <ConfirmModal
          tag="Remove"
          title="Remove bill entry?"
          message={`${fmtBillMonth(confirmRemove.bill_date)} · ${fmtOptional$(confirmRemove.amount)}. This cannot be undone.`}
          confirmLabel="Remove"
          onConfirm={async () => {
            const entryToRemove = confirmRemove;
            setRemovingEntryId(entryToRemove.id);
            setConfirmRemove(null);
            try {
              const result = await utilitiesApi.deleteEntry(bill.id, entryToRemove.id);
              onSaved(result, null);
            } catch (_) {}
            setRemovingEntryId(null);
          }}
          onCancel={() => setConfirmRemove(null)}
          loading={removingEntryId === confirmRemove.id}
        />
      )}
    </div>
  );
}

function toNumberOrNull(value) {
  if (value === "" || value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/,/g, "").replace(/[^0-9.-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === "-.") return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
}

function UtilityServiceRow({ bill, selected, detailsOpen, detailsInSheet = false, menuOpen, onMenu, onDetails, onLogBill, onEditService, onSetAutopay, onMarkPaid, onRemove, onPause, onToggleAutoLog }) {
  const spike = Number(bill.avg_spike_pct || bill.spike_pct || 0);
  const due = daysUntil(bill.due_date);
  const contract = daysUntil(bill.contract_end_date);
  const isPaid = bill.status === "paid";
  const dueSoon = !isPaid && due != null && due >= 0 && due <= 5;
  const edgeClass = utilityEdgeClass(bill, due, spike, contract);
  const canMarkPaid = (bill.recent_entries || []).some(entry => entry.status !== "paid");
  const annual = billAnnualAmount(bill);
  const entries = bill.recent_entries || [];
  const rateChanged = (() => {
    if (entries.length < 2) return false;
    const curr = Number(entries[0]?.amount || 0);
    const prev = Number(entries[1]?.amount || 0);
    if (!curr || !prev) return false;
    const pct = ((curr - prev) / prev) * 100;
    return pct >= 5 ? "up" : pct <= -5 ? "down" : false;
  })();
  const isOverdue = HOME_SERVICE_TYPES.has(bill.service_type)
    && bill.next_service_date
    && daysUntil(bill.next_service_date) != null
    && daysUntil(bill.next_service_date) < 0;
  const handleControlClick = (event, handler) => {
    event.stopPropagation();
    handler();
  };
  const handleDetailsKeyDown = (event) => {
    if (!detailsInSheet || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    onDetails();
  };
  const rowButtonProps = detailsInSheet ? {
    role: "button",
    tabIndex: 0,
    "aria-label": `Open ${bill.provider} details`,
    "aria-expanded": detailsOpen,
    onClick: onDetails,
    onKeyDown: handleDetailsKeyDown,
  } : {};
  return (
    <div
      className={`hm-util-service-row ${edgeClass}${selected ? " is-selected" : ""}${dueSoon ? " is-due-soon" : ""}`}
      {...rowButtonProps}
    >
      <div className="hm-util-service-open">
        <span className={`hm-util-service-glyph is-${bill.service_type}`}>{serviceGlyph(bill.service_type)}</span>
        <span className="hm-util-service-main">
          <strong>{bill.provider}</strong>
          <small>
            {serviceLabel(bill.service_type)}
            {bill.billing_cycle ? ` · ${bill.billing_cycle}` : ""}
            {HOME_SERVICE_TYPES.has(bill.service_type) && bill.next_service_date
              ? ` · Next: ${fmtShortDate(bill.next_service_date)}`
              : isFixedBill(bill)
                ? " · fixed monthly"
                : bill.usage_amount
                  ? ` · ${bill.usage_amount} ${bill.usage_unit || ""}`
                  : ""}
            {dueSoon && <span className="hm-util-due-meta"> · {due === 0 ? "due today" : `due in ${due}d`}</span>}
          </small>
        </span>
        <span className="hm-util-service-tags">
          {spike >= 20 && <span className="hm-util-mini-tag is-warn">+{Math.round(spike)}%</span>}
          {rateChanged === "up" && spike < 20 && <span className="hm-util-mini-tag is-warn">Rate up</span>}
          {rateChanged === "down" && spike < 20 && <span className="hm-util-mini-tag is-good">Rate down</span>}
          {isOverdue && <span className="hm-util-mini-tag is-overdue">Overdue</span>}
          {bill.status === "paused" && <span className="hm-util-mini-tag is-muted">Paused</span>}
          {bill.autopay && bill.status !== "paused" && <span className="hm-util-mini-tag">Autopay</span>}
          {bill.auto_log_enabled && bill.status !== "paused" && <span className="hm-util-auto-badge">✦ auto</span>}
          {contract != null && contract >= 0 && contract <= 45 && <span className="hm-util-mini-tag is-warn">Contract</span>}
        </span>
        <span className="hm-util-service-amount">
          <strong>{fmt$(bill.amount, 0)}</strong>
          <small className={dueSoon ? "is-urgent" : ""}>
            {isPaid ? "Paid" : due === 0 ? "Due today" : bill.due_date ? `Due ${fmtShortDate(bill.due_date)}` : "No due date"}
          </small>
          {annual > 0 && <small className="hm-util-annual-hint">{fmtCompact$(annual)}/yr</small>}
        </span>
      </div>
      <div className="hm-util-service-card-actions">
        <button type="button" onClick={(event) => handleControlClick(event, onLogBill)}>Log & history</button>
        <button type="button" className={`hm-util-details-action${detailsOpen ? " is-active" : ""}`} onClick={(event) => handleControlClick(event, onDetails)}>
          {detailsOpen ? "Hide" : "Details"}
        </button>
      </div>
      <button type="button" className="hm-util-service-menu-btn" onClick={(event) => handleControlClick(event, onMenu)} aria-label={`Open ${bill.provider} menu`}>...</button>
      {menuOpen && (
        <>
          <div className="hm-util-service-menu-backdrop" onClick={(event) => handleControlClick(event, onMenu)} />
          <div className="hm-util-service-menu-popover" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="hm-util-service-menu-close" onClick={onMenu} aria-label="Close menu">✕</button>
            {canMarkPaid && <button type="button" onClick={onMarkPaid}>✅ Mark paid</button>}
            <button type="button" className="hm-util-primary-menu-action" onClick={onEditService}>✏️ Edit service</button>
            <button type="button" onClick={onSetAutopay}>{bill.autopay ? "🔄 Edit autopay" : "🔄 Set up autopay"}</button>
            {onToggleAutoLog && (
              <button type="button" onClick={() => onToggleAutoLog(bill.id, !bill.auto_log_enabled)}>
                {bill.auto_log_enabled ? "🤖 Disable auto-log" : "🤖 Enable auto-log"}
              </button>
            )}
            <hr className="hm-util-service-menu-divider" />
            {onPause && (
              <button type="button" onClick={() => onPause(bill.id, bill.status !== "paused")}>
                {bill.status === "paused" ? "▶️ Reactivate service" : "⏸️ Pause service"}
              </button>
            )}
            <button type="button" className="is-danger" onClick={onRemove}>🗑️ Remove service</button>
          </div>
        </>
      )}
    </div>
  );
}

function utilityReportLabel(type) {
  if (type === "spike_analysis") return "Spike analysis";
  if (type === "rate_plan") return "Rate plan report";
  if (type === "provider_comparison") return "Provider comparison";
  if (type === "negotiation") return "Negotiation script";
  if (type === "efficiency") return "Annual efficiency report";
  if (type === "usage_review") return "Usage review";
  if (type === "efficiency_tips") return "Efficiency tips";
  if (type === "solar_loan_payoff") return "Loan payoff";
  if (type === "solar_savings_check") return "Savings check";
  if (type === "solar_production_review") return "Production review";
  if (type === "solar_rate_comparison") return "Rate comparison";
  if (type === "solar_credits_review") return "Credits review";
  if (type === "service_review") return "Service review";
  return "Utility review";
}

function persistedUtilityReportTypes(actions = []) {
  return [...new Set(actions.map(action => action?.report).filter(type => (
    type === "spike_analysis" ||
    type === "rate_plan" ||
    type === "provider_comparison" ||
    type === "negotiation" ||
    type === "usage_review" ||
    type === "efficiency_tips" ||
    type === "efficiency" ||
    type === "solar_loan_payoff" ||
    type === "solar_savings_check" ||
    type === "solar_production_review" ||
    type === "solar_rate_comparison" ||
    type === "solar_credits_review" ||
    type === "service_review"
  )))];
}

async function getExistingUtilityReport(bill, reportType) {
  if (reportType === "efficiency") return utilitiesApi.savedEfficiencyReport();
  return utilitiesApi.getReport(bill.id, reportType);
}

function UtilityInlineReportBody({ bill, report }) {
  if (!report) return null;
  if (report.type === "spike_analysis") return <UtilitySpikeReportBody bill={bill} result={report.result} />;
  if (report.type === "rate_plan") return <UtilityRatePlanReportBody bill={bill} result={report.result} />;
  if (report.type === "provider_comparison") return <UtilityProviderReportBody bill={bill} result={report.result} />;
  if (report.type === "negotiation") return <UtilityNegotiationReportBody bill={bill} result={report.result} />;
  if (report.type === "usage_review") return <UtilityServiceReviewBody bill={bill} result={report.result} title="Usage review" />;
  if (report.type === "efficiency_tips") return <UtilityServiceReviewBody bill={bill} result={report.result} title="Efficiency tips" />;
  if (report.type === "solar_loan_payoff") return <UtilitySolarLoanReportBody bill={bill} result={report.result} />;
  if (report.type === "solar_savings_check") return <UtilitySolarSavingsReportBody bill={bill} result={report.result} />;
  if (report.type === "solar_production_review") return <UtilitySolarProductionReportBody bill={bill} result={report.result} />;
  if (report.type === "solar_rate_comparison") return <UtilitySolarRateReportBody bill={bill} result={report.result} />;
  if (report.type === "solar_credits_review") return <UtilitySolarCreditsReportBody bill={bill} result={report.result} />;
  if (report.type === "service_review") return <UtilityServiceReviewBody bill={bill} result={report.result} title="Service review" />;
  if (report.type === "efficiency") return <UtilityEfficiencyReportBody result={report.result} />;
  return (
    <UtilityReportShell
      tone="mint"
      icon={serviceGlyph(bill.service_type)}
      title={`${serviceLabel(bill.service_type)} review`}
      eyebrow={bill.provider}
      badge="Details"
    >
      <UtilityReportSection title="Recommended next step">
        <p>{report.result?.message || "Review billing history, due dates, contract terms, and service coverage for this utility."}</p>
      </UtilityReportSection>
    </UtilityReportShell>
  );
}

function UtilityServiceInsightPanel({ bill, activeReport, reportLoading, reportError, onRunAction, onSaveRatePlan, solarContext, onEdit }) {
  const [ratePlanDraft, setRatePlanDraft] = useState(bill.rate_plan || "");
  const [savingRatePlan, setSavingRatePlan] = useState(false);
  const [ratePlanError, setRatePlanError] = useState("");
  const [reportOpen, setReportOpen] = useState(true);
  const [savedActionReports, setSavedActionReports] = useState({});
  const [savedReportOpen, setSavedReportOpen] = useState("");
  const spike = Number(bill.avg_spike_pct || bill.spike_pct || 0);
  const baseline = Number(bill.history_avg_amount || bill.avg_monthly_amount || bill.previous_amount || 0);
  const amount = billCycleAmount(bill);
  const monthly = billMonthlyAmount(bill);
  const annual = billAnnualAmount(bill);
  const overspend = amount && baseline ? Math.max(0, amount - baseline) : 0;
  const rate = bill.usage_amount ? amount / Number(bill.usage_amount || 1) : 0;
  const contractDays = daysUntil(bill.contract_end_date);
  const hasContractAlert = contractDays != null && contractDays >= 0 && contractDays <= 45;
  const actionConfig = useMemo(() => utilityActionConfig(bill), [bill]);
  const needsRatePlan = supportsRatePlanField(bill) && !bill.rate_plan;
  const showMeterFields = supportsMeterRate(bill);
  const showSolarAwareRatePlan = ["electric", "ev_charging"].includes(bill.service_type) && solarContext?.hasSolar;
  const plan = bill.rate_plan || (needsRatePlan ? "Missing" : isFixedBill(bill) ? cycleDisplayLabel(bill) : "");
  const chartValues = UtilityFallbackBarsFromBill(bill);

  useEffect(() => {
    setRatePlanDraft(bill.rate_plan || "");
    setRatePlanError("");
  }, [bill.id, bill.rate_plan]);

  useEffect(() => {
    if (activeReport) setReportOpen(true);
  }, [activeReport]);

  useEffect(() => {
    const reportTypes = persistedUtilityReportTypes(actionConfig.actions);
    setSavedActionReports({});
    setSavedReportOpen("");
    if (!bill?.id || !reportTypes.length) return undefined;
    let active = true;
    Promise.allSettled(reportTypes.map(type => getExistingUtilityReport(bill, type))).then(results => {
      if (!active) return;
      const next = {};
      reportTypes.forEach((type, index) => {
        const result = results[index];
        if (result.status === "fulfilled" && result.value) next[type] = result.value;
      });
      setSavedActionReports(next);
      // drawers start collapsed
    });
    return () => { active = false; };
  }, [bill, actionConfig.actions, activeReport?.type]);

  async function saveRatePlan() {
    const clean = ratePlanDraft.trim();
    if (!clean) {
      setRatePlanError("Enter the plan name from your utility bill.");
      return;
    }
    setSavingRatePlan(true);
    setRatePlanError("");
    try {
      await onSaveRatePlan(clean);
    } catch (err) {
      setRatePlanError(err.message || "Could not save rate plan.");
    } finally {
      setSavingRatePlan(false);
    }
  }

  return (
    <div className="hm-util-inline-insight" aria-label={`${bill.provider} utility details`}>
      {chartValues.length > 0 && (
        <div className="hm-util-inline-chart">
          <UtilityMiniBars values={chartValues} />
        </div>
      )}
      {spike >= 20 && (
        <div className="hm-util-inline-alert">
          <strong>Spike: +{Math.round(spike)}% above average</strong>
          <span>{bill.ai_cause || `${fmt$(overspend, 0)} above the usual ${fmt$(baseline, 0)} pattern.`}</span>
        </div>
      )}
      {hasContractAlert && (
        <div className="hm-util-inline-alert is-contract">
          <strong>Contract expires in {contractDays} days</strong>
          <span>{bill.contract_negotiation_tip || "Call before renewal to avoid loyalty pricing increases."}</span>
        </div>
      )}
      {needsRatePlan && (
        <div className="hm-util-inline-alert is-missing">
          <strong>Add your rate plan to unlock savings analysis</strong>
          <span>Electric rate-plan and TOU recommendations need the plan name from your bill.</span>
          <div className="hm-util-rate-plan-inline">
            <input
              className="hm-auth-input"
              value={ratePlanDraft}
              onChange={event => setRatePlanDraft(event.target.value)}
              placeholder="e.g. Time-of-use, Standard Residential"
            />
            <button type="button" disabled={savingRatePlan} onClick={saveRatePlan}>
              {savingRatePlan ? "Saving..." : "Save"}
            </button>
          </div>
          {ratePlanError && <small className="hm-util-rate-plan-error">{ratePlanError}</small>}
        </div>
      )}
      {showSolarAwareRatePlan && (
        <div className="hm-util-inline-alert is-solar-aware">
          <strong>Solar-aware rate plan analysis</strong>
          <span>
            Rate-plan AI will consider your saved solar service
            {solarContext.hasNetMetering ? " and net metering credits" : ""}.
          </span>
        </div>
      )}
      <div className="hm-util-inline-stats">
        <div><span>Annual cost</span><strong>{annual ? fmt$(annual, 0) : "Learning"}</strong></div>
        <div><span>Monthly equiv</span><strong>{monthly ? `${fmt$(monthly, 0)}/mo` : "Learning"}</strong></div>
        {showMeterFields ? (
          <>
            <div><span>Rate</span><strong>{rate ? `${fmt$(rate, 2)}/${bill.usage_unit || "unit"}` : "Needs usage"}</strong></div>
            <div><span>Plan</span><strong>{plan || "Not tracked"}</strong></div>
          </>
        ) : (
          <>
            <div><span>Cycle amount</span><strong>{amount ? fmt$(amount, 0) : "Learning"}</strong></div>
            <div><span>Cycle</span><strong>{cycleDisplayLabel(bill)}</strong></div>
          </>
        )}
      </div>
      {isAiOptimizable(bill) ? (
        actionConfig.needsSolarType ? (
          <div className="hm-util-solar-type-notice">
            <span>☀️</span>
            <div>
              <strong>Set your solar billing type</strong>
              <small>Go to Edit service and choose Loan, Lease / PPA, or Net Metering to unlock solar insights.</small>
            </div>
          </div>
        ) : actionConfig.needsSolarFinance ? (
          <div className="hm-util-solar-type-notice">
            <span>☀️</span>
            <div>
              <strong>Add solar finance details</strong>
              <small>{actionConfig.solarFinancePrompt}</small>
            </div>
          </div>
        ) : (
          <div className={`hm-util-inline-actions hm-util-inline-actions--${(actionConfig.actions || []).length}`}>
            {(actionConfig.actions || []).map(action => {
              const hasReport = activeReport?.type === action.report || savedActionReports[action.report];
              return (
                <button type="button" key={`${action.report}-${action.label}`} className={`hm-util-ai-action-btn${hasReport ? " has-report" : ""}`} onClick={() => onRunAction(action)}>
                  <span className="hm-util-ai-action-icon">{hasReport ? "↻" : "✦"}</span>
                  <span className="hm-util-ai-action-label">{action.label}</span>
                </button>
              );
            })}
          </div>
        )
      ) : (
        <div className="hm-util-service-summary-card">
          {bill.notes && <p className="hm-util-service-summary-notes">{bill.notes}</p>}
          <div className="hm-util-service-summary-grid">
            <div>
              <span>Frequency</span>
              <strong>{cycleDisplayLabel(bill)}</strong>
            </div>
            {HOME_SERVICE_TYPES.has(bill.service_type) && bill.next_service_date ? (
              <div>
                <span>Next service</span>
                <strong>{fmtShortDate(bill.next_service_date)}</strong>
              </div>
            ) : bill.due_date ? (
              <div>
                <span>Next due</span>
                <strong>{fmtShortDate(bill.due_date)}</strong>
              </div>
            ) : null}
            {bill.service_address ? (
              <div className="hm-util-service-summary-wide">
                <span>Address</span>
                <strong>{bill.service_address}</strong>
              </div>
            ) : null}
          </div>
          {bill.account_number && (
            <div className="hm-util-service-summary-account">
              <span>Account</span>
              <strong>{bill.account_number}</strong>
            </div>
          )}
        </div>
      )}
      {onEdit && (
        <div className="hm-util-inline-edit-row">
          <button type="button" className="hm-util-inline-edit-btn" onClick={onEdit}>✎ Edit service</button>
        </div>
      )}
      {reportError && <div className="hm-inline-error">{reportError}</div>}
      {Object.entries(savedActionReports)
        .filter(([type]) => activeReport?.type !== type)
        .map(([type, report]) => (
          <div className="hm-util-inline-report" key={type}>
            <SavedReportRow
              label={utilityReportLabel(type)}
              report={report}
              expanded={savedReportOpen === type}
              onToggle={() => setSavedReportOpen(savedReportOpen === type ? "" : type)}
            >
              <UtilityInlineReportBody bill={bill} report={{ type, result: report.result_json, report }} />
            </SavedReportRow>
          </div>
        ))}
      {activeReport && !reportLoading && (
        <div className="hm-util-inline-report">
          <SavedReportRow
            label={utilityReportLabel(activeReport.type)}
            report={activeReport.report || { result_json: activeReport.result, needs_refresh: false, created_at: activeReport.created_at || new Date().toISOString() }}
            expanded={reportOpen}
            onToggle={() => setReportOpen(value => !value)}
          >
            <UtilityInlineReportBody bill={bill} report={activeReport} />
          </SavedReportRow>
        </div>
      )}
    </div>
  );
}

function UtilitySheetTop({ handleRef, handleProps = {}, onClose, closeLabel }) {
  return (
    <div className="hm-util-sheet-top">
      <button
        ref={handleRef}
        type="button"
        className="hm-sheet-handle hm-util-sheet-handle"
        aria-label={closeLabel}
        {...handleProps}
      />
      <button type="button" className="hm-util-sheet-top-close" onClick={onClose} aria-label={closeLabel}>
        ×
      </button>
    </div>
  );
}

function UtilityDetailsSheet({ bill, activeReport, reportLoading, reportError, onRunAction, onSaveRatePlan, solarContext, onClose }) {
  const sheetHandleRef = useRef(null);
  const dragStartY = useRef(null);
  const dragOffsetRef = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const setSheetOffset = useCallback((value) => {
    dragOffsetRef.current = value;
    setDragOffset(value);
  }, []);

  const closeWithSlide = useCallback(() => {
    setDragging(false);
    setSheetOffset(window.innerHeight);
    window.setTimeout(onClose, 160);
  }, [onClose, setSheetOffset]);

  const beginDrag = useCallback((clientY) => {
    dragStartY.current = clientY;
    dragOffsetRef.current = 0;
    setDragging(true);
  }, []);

  const updateDrag = useCallback((clientY, event) => {
    if (dragStartY.current == null) return;
    const nextOffset = Math.max(0, clientY - dragStartY.current);
    setSheetOffset(nextOffset);
    if (nextOffset > 4) event?.preventDefault?.();
  }, [setSheetOffset]);

  const finishDrag = useCallback(() => {
    if (dragStartY.current == null) return;
    dragStartY.current = null;
    if (dragOffsetRef.current > 42) {
      closeWithSlide();
      return;
    }
    setDragging(false);
    setSheetOffset(0);
  }, [closeWithSlide, setSheetOffset]);

  useEffect(() => {
    const handle = sheetHandleRef.current;
    if (!handle) return undefined;

    function handleTouchStart(event) {
      const touch = event.touches?.[0];
      if (touch) beginDrag(touch.clientY);
    }

    function handleTouchMove(event) {
      const touch = event.touches?.[0];
      if (touch) updateDrag(touch.clientY, event);
    }

    handle.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", finishDrag);
    window.addEventListener("touchcancel", finishDrag);

    return () => {
      handle.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", finishDrag);
      window.removeEventListener("touchcancel", finishDrag);
    };
  }, [beginDrag, finishDrag, updateDrag]);

  return (
    <div className="hm-gifts-sheet-overlay fd-mobile-sheet-backdrop hm-util-details-sheet-overlay" onClick={closeWithSlide} role="dialog" aria-modal="true" aria-label={`${bill.provider} details`}>
      <button type="button" className="fd-mobile-sheet-scrim" onClick={(event) => { event.stopPropagation(); closeWithSlide(); }} aria-label="Close details" />
      <section
        className={`hm-gifts-action-sheet fd-mobile-sheet hm-util-details-sheet${dragging ? " is-dragging" : ""}`}
        style={{ transform: dragOffset ? `translateY(${dragOffset}px)` : undefined }}
        onClick={event => event.stopPropagation()}
        aria-label={`${bill.provider} details`}
      >
        <UtilitySheetTop
          handleRef={sheetHandleRef}
          onClose={closeWithSlide}
          closeLabel="Close details"
          handleProps={{
            onPointerDown: event => {
              if (event.pointerType === "mouse" && event.button !== 0) return;
              event.currentTarget.setPointerCapture?.(event.pointerId);
              beginDrag(event.clientY);
            },
            onPointerMove: event => updateDrag(event.clientY, event),
            onPointerUp: event => {
              event.currentTarget.releasePointerCapture?.(event.pointerId);
              finishDrag();
            },
            onPointerCancel: event => {
              event.currentTarget.releasePointerCapture?.(event.pointerId);
              finishDrag();
            },
          }}
        />
        <SheetLogoRow title="Details" onClose={closeWithSlide} closeLabel="Close details" showClose={false} />
        <div className="hm-sheet-body hm-util-details-sheet-body">
          <UtilityServiceInsightPanel
            bill={bill}
            activeReport={activeReport}
            reportLoading={reportLoading}
            reportError={reportError}
            onRunAction={onRunAction}
            onSaveRatePlan={onSaveRatePlan}
            solarContext={solarContext}
          />
        </div>
      </section>
    </div>
  );
}

function useUtilitySheetScrollLock(active) {
  useEffect(() => {
    if (!active || typeof document === "undefined") return undefined;
    const globalSheetOpen = document.querySelector(
      ".hm-gifts-sheet-overlay,.hm-sheet-overlay,.fd-mobile-sheet-backdrop"
    );
    if (globalSheetOpen) return undefined;
    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
    };
  }, [active]);
}

function billDateLabel(billingCycle) {
  const cycle = (billingCycle || "monthly").toLowerCase();
  if (cycle === "annual" || cycle === "yearly") return "Bill date (yearly)";
  if (cycle === "biyearly") return "Bill date (every 6 months)";
  if (cycle === "quarterly") return "Bill date (quarterly)";
  return "Bill month";
}

function defaultBillDate(billingCycle) {
  const today = new Date();
  const cycle = (billingCycle || "monthly").toLowerCase();
  if (cycle === "monthly") {
    // month picker format: YYYY-MM
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  }
  // full date for all other cycles
  return today.toISOString().slice(0, 10);
}

function isMonthly(billingCycle) {
  return !billingCycle || billingCycle.toLowerCase() === "monthly";
}

// eslint-disable-next-line no-unused-vars
function QuickLogSheet({ bill, onClose, onSaved }) {
  useBackButton(onClose);
  const monthly = isMonthly(bill.billing_cycle);
  const fixedBill = isFixedBill(bill);
  const fixedDefaultAmount = bill?.avg_monthly_amount || bill?.amount || "";
  const [amount, setAmount] = useState(() => fixedBill && fixedDefaultAmount ? String(fixedDefaultAmount) : "");
  const [usageAmount, setUsageAmount] = useState("");
  const [usageUnit, setUsageUnit] = useState(() => bill.usage_unit || defaultUnitForService(bill.service_type));
  const [billDate, setBillDate] = useState(() => defaultBillDate(bill.billing_cycle));
  const [markPaid, setMarkPaid] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Auto-compute next due date from current bill due_date + one cycle; user can override
  const autoNextDue = addMonthsToISO(
    bill.due_date || new Date().toISOString().slice(0, 10),
    cycleMonths(bill.billing_cycle)
  );
  const [nextDueDate, setNextDueDate] = useState(autoNextDue);

  const lastEntry = (bill?.recent_entries || [])[0];
  const avg = Number(bill?.avg_monthly_amount || bill?.amount || 0);
  const historyAvg = billHistoryAverage(bill?.recent_entries || [], avg);

  useUtilitySheetScrollLock(true);

  function resolvedBillDate() {
    // month picker gives "YYYY-MM", convert to first of that month
    if (monthly) return `${billDate}-01`;
    return billDate;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!amount) { setError("Enter the bill amount."); return; }
    setBusy(true);
    setError("");
    try {
      const result = await utilitiesApi.addEntry(bill.id, {
        amount: Number(amount),
        bill_date: resolvedBillDate(),
        due_date: resolvedBillDate(),
        status: markPaid ? "paid" : "active",
        payment_type: fixedBill ? "fixed" : "usage",
        usage_amount: fixedBill ? null : toNumberOrNull(usageAmount),
        usage_unit: fixedBill ? "" : usageUnit,
      });
      onSaved(result, nextDueDate);
    } catch (err) {
      setError(err.message || "Could not save bill.");
      setBusy(false);
    }
  }

  return (
    <div
      className="hm-gifts-sheet-overlay fd-mobile-sheet-backdrop hm-util-details-sheet-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Log bill · ${bill.provider}`}
      onClick={onClose}
    >
      <button type="button" className="fd-mobile-sheet-scrim" onClick={e => { e.stopPropagation(); onClose(); }} aria-label="Close" />
      <section
        className="hm-gifts-action-sheet fd-mobile-sheet hm-util-details-sheet hm-util-quick-log-sheet"
        onClick={e => e.stopPropagation()}
      >
        <UtilitySheetTop onClose={onClose} closeLabel="Close log bill" />
        <SheetLogoRow title={`Log bill · ${bill.provider}`} onClose={onClose} closeLabel="Close" showClose={false} />
        <div className="hm-sheet-body hm-util-details-sheet-body">
          <form className="hm-util-quick-log-form" onSubmit={handleSubmit}>
            {error && <div className="hm-inline-error">{error}</div>}
            {!monthly && (
              <div className="hm-util-quick-log-cycle-badge">
                {bill.billing_cycle} bill · next due auto-advances {cycleMonths(bill.billing_cycle)} month{cycleMonths(bill.billing_cycle) > 1 ? "s" : ""}
              </div>
            )}
            <div className={`hm-util-live-bill-note hm-util-log-mode-note${fixedBill ? " is-good" : ""}`}>
              {fixedBill
                ? "Fixed bill: HouseMind prefilled the regular amount. Confirm it, then save or let auto-log handle future cycles."
                : "Changing bill: enter the current amount from this statement. Usage is optional but improves trends and rate checks."}
            </div>
            <label className="hm-util-quick-log-amount-label">
              <span>{fixedBill ? "Confirmed amount *" : "Current bill amount *"}</span>
              <input
                className="hm-auth-input hm-util-quick-log-amount-input"
                type="number"
                min="0"
                step="0.01"
                placeholder={avg ? `${fixedBill ? "Fixed" : "Usual"} $${avg.toFixed(0)}` : "$0.00"}
                value={amount}
                onChange={e => setAmount(e.target.value)}
                autoFocus
              />
            </label>
            {lastEntry && (
              <p className="hm-util-last-bill-hint">
                Last logged: <strong>${Number(lastEntry.amount || 0).toFixed(0)}</strong>
                {lastEntry.bill_date ? ` · ${fmtBillMonth(lastEntry.bill_date)}` : ""}
                {!fixedBill && historyAvg ? ` · usual ${fmt$(historyAvg, 0)}` : ""}
              </p>
            )}
            <label className="hm-util-quick-log-month-label">
              <span>{billDateLabel(bill.billing_cycle)}</span>
              <input
                className="hm-auth-input"
                type={monthly ? "month" : "date"}
                value={billDate}
                onChange={e => setBillDate(e.target.value)}
              />
            </label>
            <label className="hm-util-quick-log-month-label">
              <span>{fixedBill ? "Due date" : "Next due date"} <em className="hm-util-quick-log-auto-hint">(editable)</em></span>
              <input
                className="hm-auth-input"
                type="date"
                value={nextDueDate}
                onChange={e => setNextDueDate(e.target.value)}
              />
            </label>
            {!fixedBill && (
              <div className="hm-util-live-bill-note">
                Enter the new amount from this bill. HouseMind updates trends, spike checks, and reminders instantly.
              </div>
            )}
            {!fixedBill && (
              <div className="hm-util-date-pair hm-util-usage-pair">
                <label>
                  <span>Usage optional</span>
                  <input
                    className="hm-auth-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={usageAmount}
                    onChange={e => setUsageAmount(e.target.value)}
                    placeholder={lastEntry?.usage_amount ? String(lastEntry.usage_amount) : "Usage"}
                  />
                </label>
                <label>
                  <span>Unit</span>
                  <select className="hm-auth-input" value={usageUnit || ""} onChange={e => setUsageUnit(e.target.value)}>
                    <option value="">Unit</option>
                    {UNIT_OPTIONS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                  </select>
                </label>
              </div>
            )}
            <label className="hm-util-fixed-row">
              <input type="checkbox" checked={markPaid} onChange={e => setMarkPaid(e.target.checked)} />
              <span>Mark as paid</span>
            </label>
            <button type="submit" className="hm-btn hm-btn-primary hm-util-quick-log-save" disabled={busy}>
              {busy ? "Saving…" : "Save bill"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function sortEntriesByDateDesc(entries = []) {
  return [...entries].sort((a, b) => String(b.bill_date || "").localeCompare(String(a.bill_date || "")));
}

function billEntryAmount(entry) {
  const amount = Number(entry?.amount);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

function billHistoryAverage(entries = [], fallback = 0) {
  const amounts = entries.map(billEntryAmount).filter(Boolean);
  if (!amounts.length) return Number(fallback || 0);
  return amounts.reduce((sum, amount) => sum + amount, 0) / amounts.length;
}

function HistoryTrendChart({ entries }) {
  const ordered = sortEntriesByDateDesc(entries).slice(0, 6).reverse();
  if (ordered.length < 3) {
    return (
      <div className="hm-util-history-trend-empty">
        <strong>6-month trend</strong>
        <span>Log 3 months of bills to see your trend.</span>
      </div>
    );
  }

  const amounts = ordered.map(billEntryAmount);
  const lastYearAmounts = ordered.map(entry => Number(entry.last_year_amount || entry.prior_year_amount || 0)).filter(amount => Number.isFinite(amount) && amount > 0);
  const hasLastYear = lastYearAmounts.length > 0;
  const max = Math.max(...amounts, ...lastYearAmounts, 1);
  const average = billHistoryAverage(ordered);
  return (
    <div className="hm-util-history-trend">
      <div className="hm-util-history-trend-head">
        <span>6-month trend</span>
        {average > 0 && <strong>Avg {fmt$(average, 0)}/mo</strong>}
      </div>
      <div className="hm-util-history-bars" style={{ "--hm-history-count": ordered.length }}>
        {ordered.map((entry, index) => {
          const amount = billEntryAmount(entry);
          const previous = ordered[index - 1];
          const previousAmount = billEntryAmount(previous);
          const isSpike = amount > 0 && ((average > 0 && amount > average * 1.2) || (previousAmount > 0 && amount > previousAmount * 1.2));
          return (
            <div className="hm-util-history-bar-group" key={entry.id || `${entry.bill_date}-${index}`}>
              <span>{fmt$(amount, 0)}</span>
              <span className="hm-util-history-bar-pair">
                {hasLastYear && (
                  <i
                    className="is-last-year"
                    style={{ height: `${Math.max(10, Math.round((Number(entry.last_year_amount || entry.prior_year_amount || 0) / max) * 70))}px` }}
                  />
                )}
                <i
                  className={isSpike ? "is-spike" : ""}
                  style={{ height: `${Math.max(18, Math.round((amount / max) * 70))}px` }}
                />
              </span>
              <em>{fmtBillMonth(entry.bill_date).replace(" '", " ")}</em>
            </div>
          );
        })}
      </div>
      <div className="hm-util-history-legend">
        <span><i /> This year</span>
        {hasLastYear && <span><i className="is-last-year" /> Last year</span>}
        <span><i className="is-spike" /> Spike</span>
      </div>
    </div>
  );
}

function EntryEditor({ bill, entries = [], entry, onCancel, onSaved }) {
  const monthly = isMonthly(bill.billing_cycle);
  const fixedBill = isFixedBill(bill);
  const initialForm = entryToForm(entry, bill);
  const [amount, setAmount] = useState(initialForm.amount ?? "");
  const [usageAmount, setUsageAmount] = useState(initialForm.usage_amount ?? "");
  const [usageUnit, setUsageUnit] = useState(initialForm.usage_unit || bill.usage_unit || defaultUnitForService(bill.service_type));
  const [billDate, setBillDate] = useState(() => {
    if (!initialForm.bill_date) return defaultBillDate(bill.billing_cycle);
    return monthly ? initialForm.bill_date.slice(0, 7) : initialForm.bill_date;
  });
  const [nextDueDate, setNextDueDate] = useState(initialForm.due_date || bill.due_date || "");
  const [markPaid, setMarkPaid] = useState(initialForm.status === "paid");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isEditing = Boolean(entry?.id);
  const comparisonEntries = sortEntriesByDateDesc(entries.length ? entries : (bill?.recent_entries || []))
    .filter(item => !entry?.id || item.id !== entry.id);
  const lastLogged = comparisonEntries[0] || null;
  const comparisonAverage = billHistoryAverage(comparisonEntries, bill?.avg_monthly_amount || bill?.amount || 0);
  const enteredAmount = Number(amount);
  const hasEnteredAmount = Number.isFinite(enteredAmount) && enteredAmount > 0;
  const amountDeltaPct = hasEnteredAmount && comparisonAverage > 0
    ? Math.round(((enteredAmount - comparisonAverage) / comparisonAverage) * 100)
    : null;

  function resolvedBillDate() {
    return monthly ? `${billDate}-01` : billDate;
  }

  async function submit(e) {
    e.preventDefault();
    if (!amount) {
      setError("Amount is required.");
      return;
    }
    setBusy(true);
    setError("");
    const payload = {
      ...initialForm,
      amount: toNumberOrNull(amount),
      bill_date: resolvedBillDate(),
      due_date: nextDueDate || resolvedBillDate(),
      status: markPaid ? "paid" : "active",
      payment_type: fixedBill ? "fixed" : "usage",
      usage_amount: fixedBill ? null : toNumberOrNull(usageAmount),
      usage_unit: fixedBill ? "" : usageUnit,
    };
    try {
      const result = entry?.id
        ? await utilitiesApi.updateEntry(bill.id, entry.id, payload)
        : await utilitiesApi.addEntry(bill.id, payload);
      onSaved(result);
    } catch (err) {
      setError(err.message || "Could not save monthly bill.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className={`hm-util-ledger-form hm-util-quick-log-form${isEditing ? " is-editing" : " is-quick"}`} onSubmit={submit}>
      {busy && <SavingNotice title="Saving" message={isEditing ? "Saving bill month..." : "Logging utility bill..."} />}
      {error && <div className="hm-inline-error hm-util-ledger-span">{error}</div>}
      <div className={`hm-util-live-bill-note hm-util-log-mode-note hm-util-ledger-span${fixedBill ? " is-good" : ""}`}>
        {fixedBill
          ? "Fixed bill: HouseMind uses the regular amount for this service."
          : "Changing bill: enter the current statement amount. Usage is optional but improves trends and rate checks."}
      </div>
      <label className="hm-util-quick-log-amount-label">
        <span>{fixedBill ? "Confirmed amount *" : "Current bill amount *"}</span>
        <input className="hm-auth-input hm-util-quick-log-amount-input" type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder={fixedBill ? fmtOptional$(bill.avg_monthly_amount || bill.amount) : "$0.00"} />
      </label>
      {lastLogged && (
        <div className="hm-util-last-bill-hint hm-util-ledger-span">
          Last logged: <strong>{fmt$(lastLogged.amount, 0)}</strong> · {fmtBillMonth(lastLogged.bill_date).replace(" '", " ")}
          {!fixedBill && comparisonAverage > 0 ? ` · usual ${fmt$(comparisonAverage, 0)}` : ""}
        </div>
      )}
      {amountDeltaPct != null && amountDeltaPct >= 20 && (
        <div className="hm-util-live-bill-note is-warning hm-util-ledger-span">
          {amountDeltaPct}% above your {fmt$(comparisonAverage, 0)} average — spike analysis will run automatically after saving.
        </div>
      )}
      {amountDeltaPct != null && amountDeltaPct <= -20 && (
        <div className="hm-util-live-bill-note is-good hm-util-ledger-span">
          {Math.abs(amountDeltaPct)}% below your {fmt$(comparisonAverage, 0)} average.
        </div>
      )}
      <label className="hm-util-quick-log-month-label">
        <span>{billDateLabel(bill.billing_cycle)}</span>
        <input className="hm-auth-input" type={monthly ? "month" : "date"} value={billDate} onChange={e => setBillDate(e.target.value)} />
      </label>
      <label className="hm-util-quick-log-month-label">
        <span>{fixedBill ? "Due date" : "Next due date"} <em className="hm-util-quick-log-auto-hint">(editable)</em></span>
        <input className="hm-auth-input" type="date" value={nextDueDate} onChange={e => setNextDueDate(e.target.value)} />
      </label>
      {!fixedBill && (
        <div className="hm-util-live-bill-note hm-util-ledger-span">
          Enter the new amount from this bill. HouseMind will update the trend, spike check, and reminder status instantly.
        </div>
      )}
      {!fixedBill && (
        <div className="hm-util-date-pair hm-util-usage-pair hm-util-ledger-span">
          <label>
            <span>Usage optional</span>
            <input
              className="hm-auth-input"
              type="number"
              min="0"
              step="0.01"
              value={usageAmount}
              onChange={e => setUsageAmount(e.target.value)}
              placeholder={lastLogged?.usage_amount ? String(lastLogged.usage_amount) : "Usage"}
            />
          </label>
          <label>
            <span>Unit</span>
            <select className="hm-auth-input" value={usageUnit || ""} onChange={e => setUsageUnit(e.target.value)}>
              <option value="">Unit</option>
              {UNIT_OPTIONS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
            </select>
          </label>
        </div>
      )}
      <label className="hm-util-fixed-row">
        <input type="checkbox" checked={markPaid} onChange={e => setMarkPaid(e.target.checked)} />
        <span>Mark as paid</span>
      </label>
      <div className="hm-util-ledger-actions">
        <button type="submit" className="hm-btn hm-btn-primary hm-util-quick-log-save" disabled={busy}>{busy ? "Saving..." : entry?.id ? "Save bill" : "Log bill"}</button>
        <button type="button" className="hm-util-cancel-link" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function HistoryTable({ bill, entries, editingId, removingId, onEdit, onMarkPaid, onRemove, onSaved, onCancelEdit }) {
  const [openEntryMenuId, setOpenEntryMenuId] = useState(null);
  const [showOlder, setShowOlder] = useState(false);
  const presentation = useFormPresentation();
  if (!entries.length) return <div className="hm-util-empty-line">No monthly bills logged yet.</div>;
  const defaultLimit = presentation === "sheet" ? 5 : 10;
  const visibleEntries = showOlder ? entries : entries.slice(0, defaultLimit);
  const hiddenCount = Math.max(0, entries.length - visibleEntries.length);
  return (
    <>
      <div className={`hm-util-ledger-table${showOlder ? " is-expanded" : ""}`}>
        <div className="hm-util-ledger-head">
          <span>Bill month</span>
          <span>Amount</span>
          <span>Vs prev</span>
          <span>Usage</span>
          <span>Due date</span>
          <span>Status</span>
          <span>Notes</span>
          <span></span>
        </div>
        {visibleEntries.map((entry, index) => {
          const previous = entries[index + 1];
          const diff = entry.amount != null && previous?.amount
            ? Math.round(((entry.amount - previous.amount) / previous.amount) * 100)
            : null;
          const maxVisibleAmount = Math.max(...visibleEntries.map(item => Number(item.amount || 0)), 1);
          const amountWidth = Math.max(7, Math.round((Number(entry.amount || 0) / maxVisibleAmount) * 100));
          if (editingId === entry.id) {
            return (
              <div className="hm-util-ledger-edit-row" key={entry.id}>
                <EntryEditor bill={bill} entries={entries} entry={entry} onSaved={onSaved} onCancel={onCancelEdit} />
              </div>
            );
          }
          return (
            <div className="hm-util-ledger-row" key={entry.id}>
              <span className="hm-util-ledger-date">{fmtBillMonth(entry.bill_date)}</span>
              <span>
                <strong>{fmtOptional$(entry.amount)}</strong>
              </span>
              <span className="hm-util-ledger-delta-cell">
                <i><b className={diff != null && diff >= 20 ? "is-up" : diff != null && diff < 0 ? "is-down" : ""} style={{ width: `${amountWidth}%` }} /></i>
                {diff != null ? (
                  <small className={diff > 0 ? "is-up" : diff < 0 ? "is-down" : ""}>
                    {diff > 0 ? "+" : ""}{diff}% vs prev
                  </small>
                ) : (
                  <small>First bill</small>
                )}
              </span>
              <span className="hm-util-ledger-usage">
                <span>{entry.payment_type === "fixed" ? "Fixed" : entry.usage_amount ? `${entry.usage_amount} ${entry.usage_unit || ""}`.trim() : "—"}</span>
                {entry.is_auto_logged && <span className="hm-util-auto-entry-badge">auto</span>}
                <span className="hm-util-ledger-mobile-actions">
                  <button type="button" onClick={() => onEdit(entry)} aria-label={`Edit ${fmtBillMonth(entry.bill_date)}`}>✎</button>
                  <button
                    type="button"
                    onClick={() => setOpenEntryMenuId(openEntryMenuId === entry.id ? null : entry.id)}
                    aria-expanded={openEntryMenuId === entry.id}
                    aria-label={`More actions for ${fmtBillMonth(entry.bill_date)}`}
                  >
                    ...
                  </button>
                  {openEntryMenuId === entry.id && (
                    <>
                      <span className="hm-util-ledger-backdrop" onClick={() => setOpenEntryMenuId(null)} />
                      <span className="hm-util-ledger-mobile-menu">
                        <button type="button" className="hm-util-ledger-menu-close" onClick={() => setOpenEntryMenuId(null)} aria-label="Close">✕</button>
                        {entry.status !== "paid" && (
                          <button type="button" onClick={() => { setOpenEntryMenuId(null); onMarkPaid(entry); }}>Mark paid</button>
                        )}
                        <button type="button" className="is-danger" onClick={() => { setOpenEntryMenuId(null); onRemove(entry); }} disabled={removingId === entry.id}>
                          {removingId === entry.id ? "Removing..." : "Remove entry"}
                        </button>
                      </span>
                    </>
                  )}
                </span>
              </span>
              <span>{fmtShortDate(entry.due_date)}</span>
              <span><em className={`hm-util-ledger-status is-${entry.status || "active"}`}>{entry.status || "active"}</em></span>
              <span>{entry.notes || "-"}</span>
              <span className="hm-util-ledger-row-actions">
                <button type="button" onClick={() => onEdit(entry)}>Edit</button>
                {entry.status !== "paid" && <button type="button" onClick={() => onMarkPaid(entry)}>Mark paid</button>}
                <button type="button" className="is-danger" onClick={() => onRemove(entry)} disabled={removingId === entry.id}>
                  {removingId === entry.id ? "Removing..." : "Remove"}
                </button>
              </span>
            </div>
          );
        })}
      </div>
      {entries.length > defaultLimit && (
        <button type="button" className="hm-util-history-more" onClick={() => setShowOlder(value => !value)}>
          {showOlder ? `Show latest ${defaultLimit} only` : `View older history (${hiddenCount})`}
        </button>
      )}
    </>
  );
}

function UtilityDetailPanel({ bill, onSavedService, onRemove, onToggleAutoLog, onClose }) {
  const sheetRef = useRef(null);
  const dragHandleRef = useRef(null);
  const dragStartY = useRef(null);
  const dragOffsetRef = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  useUtilitySheetScrollLock(true);

  function setSheetOffset(value) {
    dragOffsetRef.current = value;
    setDragOffset(value);
  }

  const closeWithSlide = useCallback(() => {
    setDragging(false);
    setSheetOffset(window.innerHeight);
    window.setTimeout(onClose, 160);
  }, [onClose]);

  function beginSheetDrag(clientY) {
    dragStartY.current = clientY;
    dragOffsetRef.current = 0;
    setDragging(true);
  }

  function updateSheetDrag(clientY) {
    if (dragStartY.current == null) return;
    setSheetOffset(Math.max(0, clientY - dragStartY.current));
  }

  const finishSheetDrag = useCallback(() => {
    if (dragStartY.current == null) return;
    dragStartY.current = null;
    if (dragOffsetRef.current > 42) {
      closeWithSlide();
      return;
    }
    setDragging(false);
    setSheetOffset(0);
  }, [closeWithSlide]);

  function startSheetDrag(e) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    beginSheetDrag(e.clientY);
  }

  function moveSheetDrag(e) {
    updateSheetDrag(e.clientY);
  }

  useEffect(() => {
    const handle = dragHandleRef.current;
    if (!handle) return undefined;

    function handleTouchStart(e) {
      if (!e.touches?.length) return;
      beginSheetDrag(e.touches[0].clientY);
    }

    function handleTouchMove(e) {
      if (dragStartY.current == null || !e.touches?.length) return;
      const nextOffset = Math.max(0, e.touches[0].clientY - dragStartY.current);
      setSheetOffset(nextOffset);
      if (nextOffset > 4) e.preventDefault();
    }

    function handleTouchEnd() {
      finishSheetDrag();
    }

    handle.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      handle.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [finishSheetDrag]);

  return (
    <>
    <div className="hm-util-detail-scrim" aria-hidden="true" />
    <div
      ref={sheetRef}
      className={`hm-util-inline-detail${dragging ? " is-dragging" : ""}`}
      style={{ transform: dragOffset ? `translateY(${dragOffset}px)` : undefined }}
    >
      <UtilitySheetTop
        handleRef={dragHandleRef}
        onClose={closeWithSlide}
        closeLabel="Close utility details"
        handleProps={{
          onPointerDown: startSheetDrag,
          onPointerMove: moveSheetDrag,
          onPointerUp: finishSheetDrag,
          onPointerCancel: finishSheetDrag,
        }}
      />
      <SheetLogoRow
        title={`Edit service · ${bill.provider}`}
        onClose={closeWithSlide}
        closeLabel="Close utility details"
        showClose={false}
      />
      <ServiceInfoForm bill={bill} onSaved={onSavedService} onRemove={onRemove} onToggleAutoLog={onToggleAutoLog} />
    </div>
    </>
  );
}

function ServiceInfoForm({ bill, onSaved, onRemove, onToggleAutoLog }) {
  const [form, setForm] = useState(bill);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mobileEdit, setMobileEdit] = useState(true);
  const [editStep, setEditStep] = useState(1);
  const canShowRatePlanField = supportsRatePlanField(form);
  const isSolarForm = form.service_type === "solar";
  const editStepCount = isSolarForm ? 3 : 2;
  const editStepTitle = editStep === 1 ? "Service basics" : editStep === 2 && isSolarForm ? "Solar finance" : "Settings";

  useEffect(() => {
    setForm(bill);
    setMobileEdit(true);
    setEditStep(1);
  }, [bill]);

  useEffect(() => {
    setEditStep(step => Math.min(step, editStepCount));
  }, [editStepCount]);

  function setField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function setServiceType(value) {
    const isHomeService = HOME_SERVICE_TYPES.has(value);
    setEditStep(1);
    setForm(prev => ({
      ...prev,
      service_type: value,
      solar_billing_type: value === "solar" ? prev.solar_billing_type : null,
      solar_system_cost: value === "solar" ? prev.solar_system_cost : "",
      solar_monthly_loan_payment: value === "solar" ? prev.solar_monthly_loan_payment : "",
      solar_loan_balance: value === "solar" ? prev.solar_loan_balance : "",
      solar_apr: value === "solar" ? prev.solar_apr : "",
      solar_loan_term_months: value === "solar" ? prev.solar_loan_term_months : "",
      solar_loan_start_date: value === "solar" ? prev.solar_loan_start_date : "",
      solar_estimated_monthly_production: value === "solar" ? prev.solar_estimated_monthly_production : "",
      solar_net_metering: value === "solar" ? prev.solar_net_metering : false,
      rate_plan: value === "electric" ? prev.rate_plan : "",
      service_frequency: isHomeService ? prev.service_frequency : "",
      last_service_date: isHomeService ? prev.last_service_date : "",
      next_service_date: isHomeService ? prev.next_service_date : "",
      cost_per_visit: isHomeService ? prev.cost_per_visit : "",
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const updated = await utilitiesApi.update(bill.id, {
        ...form,
        avg_monthly_amount: toNumberOrNull(form.avg_monthly_amount),
        amount: toNumberOrNull(form.amount),
        previous_amount: toNumberOrNull(form.previous_amount),
        usage_amount: toNumberOrNull(form.usage_amount),
        cost_per_visit: toNumberOrNull(form.cost_per_visit),
        solar_system_cost: toNumberOrNull(form.solar_system_cost),
        solar_monthly_loan_payment: toNumberOrNull(form.solar_monthly_loan_payment),
        solar_loan_balance: toNumberOrNull(form.solar_loan_balance),
        solar_apr: toNumberOrNull(form.solar_apr),
        solar_loan_term_months: form.solar_loan_term_months ? Number(form.solar_loan_term_months) : null,
        solar_estimated_monthly_production: toNumberOrNull(form.solar_estimated_monthly_production),
        solar_net_metering: Boolean(form.solar_net_metering || form.solar_billing_type === "loan_net_metering" || form.solar_billing_type === "net_metering"),
      });
      onSaved({ bill: updated });
      setEditStep(1);
    } catch (err) {
      setError(err.message || "Could not save service.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    {busy && <SavingNotice title="Saving" message="Saving utility service..." />}
    <div className={`hm-util-service-readout${mobileEdit ? " is-hidden" : ""}`}>
      <div><span>Provider</span><strong>{bill.provider || "-"}</strong></div>
      <div><span>Type</span><strong>{serviceLabel(bill.service_type)}</strong></div>
      <div><span>Billing cycle</span><strong>{bill.billing_cycle || <em className="hm-util-not-set">Not set</em>}</strong></div>
      <div><span>Autopay</span><strong className={bill.autopay ? "is-enabled" : ""}>{bill.autopay ? "Enabled" : "Off"}</strong></div>
      {isFixedBill(bill) && (
        <div className="hm-util-svc-row">
          <div>
            <span className="hm-util-svc-label">Auto-log</span>
            <small className="hm-util-svc-hint">
              {bill.auto_log_enabled
                ? `Logs $${bill.avg_monthly_amount || bill.amount || "—"} automatically each ${bill.billing_cycle || "month"}`
                : "HouseMind will log this bill automatically"}
            </small>
          </div>
          <button
            type="button"
            className={`hm-util-toggle${bill.auto_log_enabled ? " hm-util-toggle--on" : ""}`}
            onClick={() => onToggleAutoLog && onToggleAutoLog(bill.id, !bill.auto_log_enabled)}>
            {bill.auto_log_enabled ? "On" : "Off"}
          </button>
        </div>
      )}
      <div><span>Account #</span><strong>{bill.account_number ? maskAccount(bill.account_number) : <em className="hm-util-not-set">Not set</em>}</strong></div>
      <div><span>Monthly equiv</span><strong>{billMonthlyAmount(bill) ? fmt$(billMonthlyAmount(bill), 0) : fmtOptional$(bill.avg_monthly_amount || bill.amount)}</strong></div>
      {bill.service_type === "solar" && (
        <>
          <div><span>Loan balance</span><strong>{fmtOptional$(bill.solar_loan_balance)}</strong></div>
          <div><span>Loan payment</span><strong>{fmtOptional$(bill.solar_monthly_loan_payment)}</strong></div>
          <div><span>APR</span><strong>{bill.solar_apr ? `${bill.solar_apr}%` : <em className="hm-util-not-set">Not set</em>}</strong></div>
          <div><span>Term</span><strong>{bill.solar_loan_term_months ? `${bill.solar_loan_term_months} mo` : <em className="hm-util-not-set">Not set</em>}</strong></div>
          <div><span>Production</span><strong>{bill.solar_estimated_monthly_production ? `${bill.solar_estimated_monthly_production} kWh/mo` : <em className="hm-util-not-set">Not set</em>}</strong></div>
        </>
      )}
      {HOME_SERVICE_TYPES.has(bill.service_type) && (
        <>
          <div><span>Frequency</span><strong>{SERVICE_FREQUENCIES.find(([key]) => key === bill.service_frequency)?.[1] || <em className="hm-util-not-set">Not set</em>}</strong></div>
          <div><span>Cost / visit</span><strong>{fmtOptional$(bill.cost_per_visit)}</strong></div>
          <div><span>Last service</span><strong>{fmtDate(bill.last_service_date, "Not set")}</strong></div>
          <div><span>Next service</span><strong>{fmtDate(bill.next_service_date, "Not set")}</strong></div>
        </>
      )}
      <button type="button" className="hm-util-edit-service-btn hm-util-primary-action" onClick={() => setMobileEdit(true)}>Next</button>
    </div>
    <form className={`hm-util-service-form${mobileEdit ? " is-mobile-editing" : ""}`} onSubmit={submit}>
      <div className="hm-util-form-body">
      {error && <div className="hm-inline-error hm-util-ledger-span">{error}</div>}
      <div className="hm-util-edit-step-head hm-util-ledger-span">
        <span>Step {editStep} of {editStepCount}</span>
        <strong>{editStepTitle}</strong>
      </div>
      <div className={`hm-util-edit-step${editStep === 1 ? " is-active" : ""}`}>
      <label>
        <span>Provider</span>
        <input className="hm-auth-input" list="hm-util-provider-suggestions" value={form.provider || ""} onChange={e => setField("provider", e.target.value)} />
      </label>
      <label>
        <span>Service</span>
        <select className="hm-auth-input" value={form.service_type || "other"} onChange={e => setServiceType(e.target.value)}>
          {SERVICE_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </label>
      </div>
      {form.service_type === "solar" && (
        <div className={`hm-util-edit-step hm-util-edit-step-solar${editStep === 2 ? " is-active" : ""}`}>
        <div className="hm-util-solar-type-picker hm-util-ledger-span">
          <span className="hm-util-solar-type-label">Solar billing type</span>
          <div className="hm-util-solar-type-options">
            {[
              ["loan", "🏦 Loan", "Paying off a solar panel loan"],
              ["loan_net_metering", "🏦 Loan + Net Metering", "Own panels with a loan and receive grid export credits"],
              ["lease_ppa", "📋 Lease / PPA", "Pay for power from leased panels"],
              ["net_metering", "⚡ Net Metering", "Own panels, sell excess to grid"],
            ].map(([val, label, hint]) => (
              <button
                key={val}
                type="button"
                className={`hm-util-solar-type-btn${form.solar_billing_type === val ? " is-selected" : ""}`}
                onClick={() => setField("solar_billing_type", val)}
                title={hint}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        </div>
      )}
      {form.service_type === "solar" && (
        <div className={`hm-util-edit-step hm-util-edit-step-solar${editStep === 2 ? " is-active" : ""}`}>
        <div className="hm-util-solar-finance-grid hm-util-ledger-span">
          <span className="hm-util-solar-type-label">Solar finance</span>
          <label>
            <span>System cost</span>
            <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.solar_system_cost || ""} onChange={e => setField("solar_system_cost", e.target.value)} />
          </label>
          <label>
            <span>Loan payment</span>
            <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.solar_monthly_loan_payment || ""} onChange={e => setField("solar_monthly_loan_payment", e.target.value)} />
          </label>
          <label>
            <span>Loan balance</span>
            <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.solar_loan_balance || ""} onChange={e => setField("solar_loan_balance", e.target.value)} />
          </label>
          <label>
            <span>APR %</span>
            <input className="hm-auth-input" type="number" min="0" step="0.001" value={form.solar_apr || ""} onChange={e => setField("solar_apr", e.target.value)} />
          </label>
          <label>
            <span>Loan term months</span>
            <input className="hm-auth-input" type="number" min="1" step="1" value={form.solar_loan_term_months || ""} onChange={e => setField("solar_loan_term_months", e.target.value)} />
          </label>
          <label>
            <span>Loan start</span>
            <input className="hm-auth-input" type="date" value={form.solar_loan_start_date || ""} onChange={e => setField("solar_loan_start_date", e.target.value)} />
          </label>
          <label>
            <span>Monthly production kWh</span>
            <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.solar_estimated_monthly_production || ""} onChange={e => setField("solar_estimated_monthly_production", e.target.value)} />
          </label>
          <button type="button" className={`hm-util-toggle-row hm-util-ledger-span${form.solar_net_metering || form.solar_billing_type === "loan_net_metering" || form.solar_billing_type === "net_metering" ? " is-on" : ""}`} onClick={() => setField("solar_net_metering", !form.solar_net_metering)}>
            <span>{form.solar_net_metering || form.solar_billing_type === "loan_net_metering" || form.solar_billing_type === "net_metering" ? "✓" : ""}</span>
            <strong>Net metering</strong>
            <small>Use export credits in electric rate-plan analysis</small>
          </button>
        </div>
        </div>
      )}
      <div className={`hm-util-edit-step${editStep === 1 ? " is-active" : ""}`}>
      <label>
        <span>Billing cycle</span>
        <select className="hm-auth-input" value={form.billing_cycle || ""} onChange={e => setField("billing_cycle", e.target.value)}>
          {BILLING_CYCLES.map(([key, label]) => <option key={key || "none"} value={key}>{label}</option>)}
        </select>
      </label>
      <label>
        <span>{utilityAmountFieldLabel(form)}</span>
        <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.avg_monthly_amount || ""} onChange={e => setField("avg_monthly_amount", e.target.value)} />
      </label>
      <label>
        <span>Account number</span>
        <input className="hm-auth-input" value={form.account_number || ""} onChange={e => setField("account_number", e.target.value)} />
      </label>
      {canShowRatePlanField && (
        <label>
          <span>Electric rate plan</span>
          <input className="hm-auth-input" value={form.rate_plan || ""} onChange={e => setField("rate_plan", e.target.value)} placeholder="e.g. Standard Residential, TOU" />
        </label>
      )}
      <label>
        <span>Contract ends</span>
        <input className="hm-auth-input" type="date" value={form.contract_end_date || ""} onChange={e => setField("contract_end_date", e.target.value)} />
      </label>
      </div>
      <div className={`hm-util-edit-step${editStep === editStepCount ? " is-active" : ""}`}>
      {HOME_SERVICE_TYPES.has(form.service_type) && (
        <>
          <label>
            <span>Service frequency</span>
            <select className="hm-auth-input" value={form.service_frequency || ""} onChange={e => setField("service_frequency", e.target.value)}>
              {SERVICE_FREQUENCIES.map(([key, label]) => <option key={key || "none"} value={key}>{label}</option>)}
            </select>
          </label>
          <label>
            <span>Cost per visit</span>
            <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.cost_per_visit || ""} onChange={e => setField("cost_per_visit", e.target.value)} />
          </label>
          <label>
            <span>Last service</span>
            <input className="hm-auth-input" type="date" value={form.last_service_date || ""} onChange={e => setField("last_service_date", e.target.value)} />
          </label>
          <label>
            <span>Next service</span>
            <input className="hm-auth-input" type="date" value={form.next_service_date || ""} onChange={e => setField("next_service_date", e.target.value)} />
          </label>
        </>
      )}
      <label className="hm-util-fixed-row">
        <input type="checkbox" checked={Boolean(form.autopay)} onChange={e => setField("autopay", e.target.checked)} />
        <span>Autopay</span>
      </label>
      {form.autopay && (
        <label>
          <span>Autopay date</span>
          <select className="hm-auth-input" value={String(form.autopay_date || "")} onChange={e => setField("autopay_date", e.target.value)}>
            <option value="">Select day</option>
            {form.autopay_date && !AUTOPAY_DAY_OPTIONS.includes(String(form.autopay_date)) && (
              <option value={form.autopay_date}>{form.autopay_date}</option>
            )}
            {AUTOPAY_DAY_OPTIONS.map(day => <option key={day} value={day}>Day {day}</option>)}
          </select>
        </label>
      )}
      <label className="hm-util-ledger-span">
        <span>Service address</span>
        <input className="hm-auth-input" value={form.service_address || ""} onChange={e => setField("service_address", e.target.value)} />
      </label>
      <label className="hm-util-ledger-span">
        <span>Notes</span>
        <textarea className="hm-auth-input" rows={3} value={form.notes || ""} onChange={e => setField("notes", e.target.value)} />
      </label>
      </div>
      </div>
      <div className="hm-util-ledger-actions hm-util-ledger-span">
        {editStep < editStepCount ? (
          <button type="button" className="hm-util-primary-action" disabled={busy} onClick={() => setEditStep(step => Math.min(editStepCount, step + 1))}>Next</button>
        ) : (
          <button type="submit" className="hm-util-primary-action" disabled={busy}>{busy ? "Saving..." : "Save service"}</button>
        )}
        {editStep > 1 ? (
          <button type="button" className="hm-util-cancel-link" onClick={() => setEditStep(step => Math.max(1, step - 1))}>Back</button>
        ) : (
          <button type="button" className="hm-util-cancel-link" onClick={() => setMobileEdit(false)}>Cancel</button>
        )}
        <button type="button" className="is-danger" onClick={() => onRemove(bill.id)}>Remove service</button>
      </div>
    </form>
    </>
  );
}

function AddServiceDrawer({ open, initialType, onClose, onSaved }) {
  useBackButton(onClose, open);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...BLANK_BILL, service_type: initialType || "electric" });
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanConsent, setScanConsent] = useState(false);
  const [scanReviewed, setScanReviewed] = useState(false);
  const [firstBillScanOpen, setFirstBillScanOpen] = useState(false);
  const [drawerDragOffset, setDrawerDragOffset] = useState(0);
  const [drawerDragging, setDrawerDragging] = useState(false);
  const [scanSheetDragOffset, setScanSheetDragOffset] = useState(0);
  const [scanSheetDragging, setScanSheetDragging] = useState(false);
  const [error, setError] = useState("");
  const firstBillUploadRef = useRef(null);
  const firstBillCameraRef = useRef(null);
  const drawerHandleRef = useRef(null);
  const drawerDragStartY = useRef(null);
  const drawerDragOffsetRef = useRef(0);
  const scanSheetHandleRef = useRef(null);
  const scanSheetDragStartY = useRef(null);
  const scanSheetDragOffsetRef = useRef(0);
  const presentation = useFormPresentation();
  const fixed = FIXED_TYPES.has(form.service_type);
  useUtilitySheetScrollLock(open);

  useEffect(() => {
    if (open) {
      setStep(1);
      setForm({ ...BLANK_BILL, service_type: initialType || "electric" });
      setError("");
      setScanning(false);
      setScanConsent(false);
      setScanReviewed(false);
      setFirstBillScanOpen(false);
      setDrawerDragOffset(0);
      setDrawerDragging(false);
      setScanSheetDragOffset(0);
      setScanSheetDragging(false);
      drawerDragStartY.current = null;
      drawerDragOffsetRef.current = 0;
      scanSheetDragStartY.current = null;
      scanSheetDragOffsetRef.current = 0;
    }
  }, [open, initialType]);

  function setField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function chooseType(type) {
    const isHomeService = HOME_SERVICE_TYPES.has(type);
    setForm(prev => ({
      ...prev,
      service_type: type,
      solar_billing_type: type === "solar" ? prev.solar_billing_type : null,
      solar_system_cost: type === "solar" ? prev.solar_system_cost : "",
      solar_monthly_loan_payment: type === "solar" ? prev.solar_monthly_loan_payment : "",
      solar_loan_balance: type === "solar" ? prev.solar_loan_balance : "",
      solar_apr: type === "solar" ? prev.solar_apr : "",
      solar_loan_term_months: type === "solar" ? prev.solar_loan_term_months : "",
      solar_loan_start_date: type === "solar" ? prev.solar_loan_start_date : "",
      solar_estimated_monthly_production: type === "solar" ? prev.solar_estimated_monthly_production : "",
      solar_net_metering: type === "solar" ? prev.solar_net_metering : false,
      rate_plan: type === "electric" ? prev.rate_plan : "",
      usage_unit: defaultUnitForService(type),
      usage_amount: FIXED_TYPES.has(type) ? "" : prev.usage_amount,
      service_frequency: isHomeService ? prev.service_frequency : "",
      last_service_date: isHomeService ? prev.last_service_date : "",
      next_service_date: isHomeService ? prev.next_service_date : "",
      cost_per_visit: isHomeService ? prev.cost_per_visit : "",
    }));
  }

  function setFirstBillDate(value) {
    setForm(prev => ({
      ...prev,
      billing_period_end: value,
      due_date: prev.due_date || addDaysISO(value, 30),
    }));
  }

  const resetFirstBillScanSheet = useCallback(() => {
    setScanSheetDragging(false);
    setScanSheetDragOffset(0);
    scanSheetDragStartY.current = null;
    scanSheetDragOffsetRef.current = 0;
    setFirstBillScanOpen(false);
  }, []);

  async function handleFirstBillScan(file) {
    if (!file) return;
    if (!scanConsent) {
      setError("Confirm the scan notice before uploading a bill.");
      return;
    }
    setScanning(true);
    setError("");
    try {
      const result = await utilitiesApi.scanBill(file);
      const scanned = result.suggested_entry || {};
      const service = result.suggested_service_updates || {};
      const nextServiceType = service.service_type || form.service_type;
      setForm(prev => ({
        ...prev,
        provider: service.provider || prev.provider,
        service_type: nextServiceType,
        account_number: service.account_number || prev.account_number,
        rate_plan: nextServiceType === "electric" ? (service.rate_plan || prev.rate_plan) : "",
        amount: scanned.amount ?? prev.amount,
        due_date: scanned.due_date || prev.due_date,
        billing_period_end: scanned.bill_date || service.billing_period_end || prev.billing_period_end,
        billing_period_start: service.billing_period_start || prev.billing_period_start,
        usage_amount: scanned.usage_amount ?? prev.usage_amount,
        usage_unit: scanned.usage_unit || prev.usage_unit || defaultUnitForService(service.service_type || prev.service_type),
        status: scanned.status || prev.status,
        notes: scanned.notes || prev.notes,
      }));
      setScanReviewed(true);
      resetFirstBillScanSheet();
      setStep(2);
    } catch (err) {
      setError(err.message || "Could not scan this bill.");
    } finally {
      setScanning(false);
    }
  }

  function openFirstBillPicker(ref) {
    if (!scanConsent) {
      setError("Confirm the scan notice before uploading a bill.");
      return;
    }
    ref.current?.click();
  }

  const setDrawerOffset = useCallback((value) => {
    drawerDragOffsetRef.current = value;
    setDrawerDragOffset(value);
  }, []);

  const closeWithSlide = useCallback(() => {
    setDrawerDragging(false);
    setDrawerOffset(window.innerHeight);
    window.setTimeout(onClose, 160);
  }, [onClose, setDrawerOffset]);

  const beginDrawerDrag = useCallback((clientY) => {
    drawerDragStartY.current = clientY;
    drawerDragOffsetRef.current = 0;
    setDrawerDragging(true);
  }, []);

  const updateDrawerDrag = useCallback((clientY, event) => {
    if (drawerDragStartY.current == null) return;
    const nextOffset = Math.max(0, clientY - drawerDragStartY.current);
    setDrawerOffset(nextOffset);
    if (nextOffset > 4) event?.preventDefault?.();
  }, [setDrawerOffset]);

  const finishDrawerDrag = useCallback(() => {
    if (drawerDragStartY.current == null) return;
    drawerDragStartY.current = null;
    if (drawerDragOffsetRef.current > 42) {
      closeWithSlide();
      return;
    }
    setDrawerDragging(false);
    setDrawerOffset(0);
  }, [closeWithSlide, setDrawerOffset]);

  useEffect(() => {
    if (!open || presentation === "modal") return undefined;
    const handle = drawerHandleRef.current;
    if (!handle) return undefined;

    function handleTouchStart(event) {
      const touch = event.touches?.[0];
      if (touch) beginDrawerDrag(touch.clientY);
    }

    function handleTouchMove(event) {
      const touch = event.touches?.[0];
      if (touch) updateDrawerDrag(touch.clientY, event);
    }

    handle.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", finishDrawerDrag);
    window.addEventListener("touchcancel", finishDrawerDrag);

    return () => {
      handle.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", finishDrawerDrag);
      window.removeEventListener("touchcancel", finishDrawerDrag);
    };
  }, [beginDrawerDrag, finishDrawerDrag, open, presentation, updateDrawerDrag]);

  const setScanSheetOffset = useCallback((value) => {
    scanSheetDragOffsetRef.current = value;
    setScanSheetDragOffset(value);
  }, []);

  const closeFirstBillScanSheet = useCallback(() => {
    setScanSheetDragging(false);
    setScanSheetOffset(window.innerHeight);
    window.setTimeout(() => {
      resetFirstBillScanSheet();
    }, 160);
  }, [resetFirstBillScanSheet, setScanSheetOffset]);

  const beginScanSheetDrag = useCallback((clientY) => {
    scanSheetDragStartY.current = clientY;
    scanSheetDragOffsetRef.current = 0;
    setScanSheetDragging(true);
  }, []);

  const updateScanSheetDrag = useCallback((clientY, event) => {
    if (scanSheetDragStartY.current == null) return;
    const nextOffset = Math.max(0, clientY - scanSheetDragStartY.current);
    setScanSheetOffset(nextOffset);
    if (nextOffset > 4) event?.preventDefault?.();
  }, [setScanSheetOffset]);

  const finishScanSheetDrag = useCallback(() => {
    if (scanSheetDragStartY.current == null) return;
    scanSheetDragStartY.current = null;
    if (scanSheetDragOffsetRef.current > 42) {
      closeFirstBillScanSheet();
      return;
    }
    setScanSheetDragging(false);
    setScanSheetOffset(0);
  }, [closeFirstBillScanSheet, setScanSheetOffset]);

  useEffect(() => {
    if (!firstBillScanOpen) return undefined;
    const handle = scanSheetHandleRef.current;
    if (!handle) return undefined;

    function handleTouchStart(event) {
      const touch = event.touches?.[0];
      if (touch) beginScanSheetDrag(touch.clientY);
    }

    function handleTouchMove(event) {
      const touch = event.touches?.[0];
      if (touch) updateScanSheetDrag(touch.clientY, event);
    }

    handle.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", finishScanSheetDrag);
    window.addEventListener("touchcancel", finishScanSheetDrag);

    return () => {
      handle.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", finishScanSheetDrag);
      window.removeEventListener("touchcancel", finishScanSheetDrag);
    };
  }, [beginScanSheetDrag, finishScanSheetDrag, firstBillScanOpen, updateScanSheetDrag]);

  if (!open) return null;

  async function save({ skipFirstBill = false } = {}) {
    if (!form.provider.trim()) {
      setStep(2);
      setError("Provider is required.");
      return;
    }
    const payload = skipFirstBill ? {
      ...form,
      skip_first_bill: true,
      amount: "",
      previous_amount: "",
      due_date: "",
      billing_period_start: "",
      billing_period_end: "",
      usage_amount: "",
      usage_unit: defaultUnitForService(form.service_type),
      status: "active",
      notes: "",
    } : form;
    const payloadWithDefaults = skipFirstBill ? payload : {
      ...payload,
      billing_period_end: payload.billing_period_end || todayISO(),
      due_date: payload.due_date || addDaysISO(payload.billing_period_end || todayISO(), 30),
    };
    const payloadFixed = FIXED_TYPES.has(payloadWithDefaults.service_type);
    setBusy(true);
    setError("");
    try {
      const result = await utilitiesApi.create({
        ...payloadWithDefaults,
        avg_monthly_amount: toNumberOrNull(payloadWithDefaults.avg_monthly_amount),
        amount: toNumberOrNull(payloadWithDefaults.amount),
        previous_amount: toNumberOrNull(payloadWithDefaults.previous_amount),
        usage_amount: payloadFixed ? null : toNumberOrNull(payloadWithDefaults.usage_amount),
        cost_per_visit: toNumberOrNull(payloadWithDefaults.cost_per_visit),
        solar_system_cost: toNumberOrNull(payloadWithDefaults.solar_system_cost),
        solar_monthly_loan_payment: toNumberOrNull(payloadWithDefaults.solar_monthly_loan_payment),
        solar_loan_balance: toNumberOrNull(payloadWithDefaults.solar_loan_balance),
        solar_apr: toNumberOrNull(payloadWithDefaults.solar_apr),
        solar_loan_term_months: payloadWithDefaults.solar_loan_term_months ? Number(payloadWithDefaults.solar_loan_term_months) : null,
        solar_estimated_monthly_production: toNumberOrNull(payloadWithDefaults.solar_estimated_monthly_production),
        solar_net_metering: Boolean(payloadWithDefaults.solar_net_metering || payloadWithDefaults.solar_billing_type === "loan_net_metering" || payloadWithDefaults.solar_billing_type === "net_metering"),
        payment_type: payloadFixed ? "fixed" : "usage",
      });
      onSaved({ bill: result });
    } catch (err) {
      setError(err.message || "Could not save utility.");
    } finally {
      setBusy(false);
    }
  }

  const drawerContent = (
    <>
        {busy && <SavingNotice title="Saving" message="Saving utility service..." />}
        {scanning && <SavingNotice title="Scanning" message="Reading utility bill..." />}
        {presentation !== "modal" && (
          <UtilitySheetTop
            handleRef={drawerHandleRef}
            onClose={closeWithSlide}
            closeLabel="Close add service"
            handleProps={{
              onPointerDown: event => {
                if (event.pointerType === "mouse" && event.button !== 0) return;
                event.currentTarget.setPointerCapture?.(event.pointerId);
                beginDrawerDrag(event.clientY);
              },
              onPointerMove: event => updateDrawerDrag(event.clientY, event),
              onPointerUp: event => {
                event.currentTarget.releasePointerCapture?.(event.pointerId);
                finishDrawerDrag();
              },
              onPointerCancel: event => {
                event.currentTarget.releasePointerCapture?.(event.pointerId);
                finishDrawerDrag();
              },
            }}
          />
        )}
        <SheetLogoRow title="Add a service" onClose={presentation === "modal" ? onClose : closeWithSlide} closeLabel="Close add service" showClose={presentation === "modal"} />
        <div className="hm-util-step-indicator" aria-label={`Step ${step} of 3`}>
          {[1, 2, 3].map(item => <span key={item} className={item === step ? "is-active" : item < step ? "is-done" : ""} />)}
          <em>Step {step} of 3</em>
        </div>
        {error && <div className="hm-inline-error">{error}</div>}

        {step === 1 && (
          <div className="hm-util-type-picker hm-util-type-picker--compact">
            <div className="hm-util-start-choices hm-util-ledger-span">
              <button type="button" className={`hm-util-scan-launch${scanReviewed ? " is-complete" : ""}`} onClick={() => { setError(""); setFirstBillScanOpen(true); }} disabled={busy || scanning}>
                <span>📄</span>
                <div>
                  <strong>{scanReviewed ? "Statement scanned" : "Scan bill or statement"}</strong>
                  <small>{scanReviewed ? "Review the filled service details next." : "Prefill service and first bill details."}</small>
                </div>
              </button>
              <div className="hm-util-manual-choice">
                <span>✎</span>
                <div>
                  <strong>Enter manually</strong>
                  <small>Choose the service type below.</small>
                </div>
              </div>
            </div>
            <div className="hm-util-type-primary-grid">
              {SERVICE_TYPES.filter(([key]) => PRIMARY_ADD_TYPES.includes(key)).map(([key, label]) => (
                <button key={key} type="button" className={form.service_type === key ? "is-selected" : ""} onClick={() => chooseType(key)}>
                  <span>{serviceGlyph(key)}</span>
                  <strong>{serviceTileLabel(key, label)}</strong>
                </button>
              ))}
            </div>
            <div className="hm-util-type-pill-grid">
              {SERVICE_TYPES.filter(([key]) => SECONDARY_ADD_TYPES.includes(key)).map(([key, label]) => (
                <button key={key} type="button" className={form.service_type === key ? "is-selected" : ""} onClick={() => chooseType(key)}>
                  <span>{serviceGlyph(key)}</span>
                  <strong>{serviceTileLabel(key, label)}</strong>
                </button>
              ))}
            </div>
            <details className="hm-util-more-types hm-util-more-types--compact">
              <summary>+ More: lawn, pest, HOA, pool, security...</summary>
              <div>
                {SERVICE_TYPES.filter(([key]) => MORE_ADD_TYPES.includes(key)).map(([key, label]) => (
                  <button key={key} type="button" className={form.service_type === key ? "is-selected" : ""} onClick={() => chooseType(key)}>
                    <span>{serviceGlyph(key)}</span>
                    <strong>{serviceTileLabel(key, label)}</strong>
                  </button>
                ))}
              </div>
            </details>
          </div>
        )}

        {step === 2 && (
          <div className="hm-util-drawer-form">
            <label className="hm-util-ledger-span">
              <span>Provider</span>
              <input className="hm-auth-input" list="hm-util-provider-suggestions" value={form.provider} onChange={e => setField("provider", e.target.value)} placeholder="NV Energy, Spectrum..." />
            </label>
            <label>
              <span>Billing cycle</span>
              <select className="hm-auth-input" value={form.billing_cycle} onChange={e => setField("billing_cycle", e.target.value)}>
                {BILLING_CYCLES.map(([key, label]) => <option key={key || "none"} value={key}>{label}</option>)}
              </select>
            </label>
            <label>
              <span>{utilityAmountFieldLabel(form)}</span>
              <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.avg_monthly_amount} onChange={e => setField("avg_monthly_amount", e.target.value)} placeholder="0.00" />
            </label>
            <label>
              <span>Account optional</span>
              <input className="hm-auth-input" value={form.account_number} onChange={e => setField("account_number", e.target.value)} placeholder="Last 4 or account nickname" />
            </label>
            {supportsRatePlanField(form) && (
              <label className="hm-util-ledger-span">
                <span>Electric rate plan</span>
                <input
                  className="hm-auth-input"
                  value={form.rate_plan || ""}
                  onChange={e => setField("rate_plan", e.target.value)}
                  placeholder="e.g. Standard Residential, TOU"
                />
              </label>
            )}
            {form.service_type === "solar" && (
              <div className="hm-util-solar-type-picker hm-util-ledger-span">
                <span className="hm-util-solar-type-label">Solar billing type</span>
                <div className="hm-util-solar-type-options">
                  {[
                    ["loan", "🏦 Loan", "You own the panels, paying off a loan"],
                    ["loan_net_metering", "🏦 Loan + Net Metering", "You own panels with a loan and receive grid export credits"],
                    ["lease_ppa", "📋 Lease / PPA", "You pay for power produced by leased panels"],
                    ["net_metering", "⚡ Net Metering", "You own panels and sell excess back to grid"],
                  ].map(([val, label, hint]) => (
                    <button
                      key={val}
                      type="button"
                      className={`hm-util-solar-type-btn${form.solar_billing_type === val ? " is-selected" : ""}`}
                      onClick={() => setField("solar_billing_type", val)}
                      title={hint}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {form.service_type === "solar" && (
              <div className="hm-util-solar-finance-grid hm-util-ledger-span">
                <span className="hm-util-solar-type-label">Solar finance</span>
                <label>
                  <span>System cost</span>
                  <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.solar_system_cost || ""} onChange={e => setField("solar_system_cost", e.target.value)} />
                </label>
                <label>
                  <span>Loan payment</span>
                  <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.solar_monthly_loan_payment || ""} onChange={e => setField("solar_monthly_loan_payment", e.target.value)} />
                </label>
                <label>
                  <span>Loan balance</span>
                  <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.solar_loan_balance || ""} onChange={e => setField("solar_loan_balance", e.target.value)} />
                </label>
                <label>
                  <span>APR %</span>
                  <input className="hm-auth-input" type="number" min="0" step="0.001" value={form.solar_apr || ""} onChange={e => setField("solar_apr", e.target.value)} />
                </label>
                <label>
                  <span>Loan term months</span>
                  <input className="hm-auth-input" type="number" min="1" step="1" value={form.solar_loan_term_months || ""} onChange={e => setField("solar_loan_term_months", e.target.value)} />
                </label>
                <label>
                  <span>Loan start</span>
                  <input className="hm-auth-input" type="date" value={form.solar_loan_start_date || ""} onChange={e => setField("solar_loan_start_date", e.target.value)} />
                </label>
                <label>
                  <span>Monthly production kWh</span>
                  <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.solar_estimated_monthly_production || ""} onChange={e => setField("solar_estimated_monthly_production", e.target.value)} />
                </label>
                <button type="button" className={`hm-util-toggle-row hm-util-ledger-span${form.solar_net_metering || form.solar_billing_type === "loan_net_metering" || form.solar_billing_type === "net_metering" ? " is-on" : ""}`} onClick={() => setField("solar_net_metering", !form.solar_net_metering)}>
                  <span>{form.solar_net_metering || form.solar_billing_type === "loan_net_metering" || form.solar_billing_type === "net_metering" ? "✓" : ""}</span>
                  <strong>Net metering</strong>
                  <small>Use export credits in electric rate-plan analysis</small>
                </button>
              </div>
            )}
            <button type="button" className={`hm-util-toggle-row hm-util-ledger-span${form.autopay ? " is-on" : ""}`} onClick={() => setField("autopay", !form.autopay)}>
              <span>{form.autopay ? "✓" : ""}</span>
              <strong>Autopay</strong>
              <small>{form.autopay ? "Enabled" : "Tap if this bill pays automatically"}</small>
            </button>
            {form.autopay && (
              <label className="hm-util-ledger-span">
                <span>Autopay date</span>
                <select className="hm-auth-input" value={String(form.autopay_date || "")} onChange={e => setField("autopay_date", e.target.value)}>
                  <option value="">Select day of month</option>
                  {form.autopay_date && !AUTOPAY_DAY_OPTIONS.includes(String(form.autopay_date)) && (
                    <option value={form.autopay_date}>{form.autopay_date}</option>
                  )}
                  {AUTOPAY_DAY_OPTIONS.map(day => <option key={day} value={day}>Day {day}</option>)}
                </select>
              </label>
            )}
            {form.autopay && Number(form.avg_monthly_amount) > 0 && (
              <div className="hm-util-autolog-offer hm-util-ledger-span">
                <span>✦</span>
                <div>
                  <strong>{fixed ? "Auto-log will turn on" : "Manual update reminders will turn on"}</strong>
                  <small>
                    {fixed
                      ? `HouseMind will log ${fmt$(form.avg_monthly_amount)} every ${form.billing_cycle || "month"} and send a bell notification each time.`
                      : "This bill changes month to month, so HouseMind will remind you to enter the current amount instead of logging a guess."}
                  </small>
                </div>
              </div>
            )}
            {form.autopay && !Number(form.avg_monthly_amount) && (
              <div className="hm-util-autolog-offer is-muted hm-util-ledger-span">
                <span>✦</span>
                <div>
                  <strong>{fixed ? "Auto-log ready" : "Bill reminders ready"}</strong>
                  <small>
                    {fixed
                      ? "Set the regular amount above so HouseMind can log this fixed bill automatically."
                      : "Set a typical amount for budgeting; HouseMind will still ask for the real amount each cycle."}
                  </small>
                </div>
              </div>
            )}
            {HOME_SERVICE_TYPES.has(form.service_type) && (
              <div className="hm-util-service-extra-grid hm-util-ledger-span">
                <label>
                  <span>Frequency</span>
                  <select className="hm-auth-input" value={form.service_frequency} onChange={e => setField("service_frequency", e.target.value)}>
                    {SERVICE_FREQUENCIES.map(([key, label]) => <option key={key || "none"} value={key}>{label}</option>)}
                  </select>
                </label>
                <label>
                  <span>Cost / visit</span>
                  <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.cost_per_visit} onChange={e => setField("cost_per_visit", e.target.value)} placeholder="0.00" />
                </label>
                <label>
                  <span>Last service</span>
                  <input className="hm-auth-input" type="date" value={form.last_service_date} onChange={e => setField("last_service_date", e.target.value)} />
                </label>
                <label>
                  <span>Next service</span>
                  <input className="hm-auth-input" type="date" value={form.next_service_date} onChange={e => setField("next_service_date", e.target.value)} />
                </label>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="hm-util-drawer-form">
            <div className="hm-util-form-section hm-util-ledger-span">
              <strong>💡 First bill</strong>
              <span>{scanReviewed ? "Confirm the scanned bill details before saving." : "Log only the amount and due date, or skip this optional step."}</span>
            </div>
            <label className="hm-util-ledger-span">
              <span>Bill amount</span>
              <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.amount} onChange={e => setField("amount", e.target.value)} />
            </label>
            <div className="hm-util-date-pair hm-util-ledger-span">
              <label>
                <span>Bill date</span>
                <input className="hm-auth-input" type="date" value={form.billing_period_end || todayISO()} onChange={e => setFirstBillDate(e.target.value)} />
              </label>
              <label>
                <span>Due date</span>
                <input className="hm-auth-input" type="date" value={form.due_date || addDaysISO(form.billing_period_end || todayISO(), 30)} onChange={e => setField("due_date", e.target.value)} />
              </label>
            </div>
            {!fixed && (
              <div className="hm-util-date-pair hm-util-usage-pair hm-util-ledger-span">
                <label>
                  <span>Usage optional</span>
                  <input className="hm-auth-input" type="number" min="0" step="0.01" value={form.usage_amount} onChange={e => setField("usage_amount", e.target.value)} />
                </label>
                <label>
                  <span>Unit</span>
                  <select className="hm-auth-input" value={form.usage_unit || defaultUnitForService(form.service_type)} onChange={e => setField("usage_unit", e.target.value)}>
                    <option value="">Unit</option>
                    {UNIT_OPTIONS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                  </select>
                </label>
              </div>
            )}
            <button type="button" className="hm-util-skip-bill hm-util-ledger-span" disabled={busy} onClick={() => save({ skipFirstBill: true })}>
              Add service without bill history
              <small>Use this if you do not have the current bill yet. You can log it later.</small>
            </button>
          </div>
        )}

        <div className="hm-util-drawer-footer">
          {step > 1 ? (
            <button type="button" className="hm-util-back-link" onClick={() => setStep(step - 1)}>← Back</button>
          ) : (
            <button type="button" className="hm-util-back-link" onClick={presentation === "modal" ? onClose : closeWithSlide}>Cancel</button>
          )}
          {step < 3 ? (
            <button type="button" onClick={() => setStep(step + 1)}>Next</button>
          ) : (
            <button type="button" className="is-primary" disabled={busy} onClick={() => save()}>{busy ? "Saving..." : "Save service"}</button>
          )}
        </div>
    </>
  );

  const firstBillScanSheet = firstBillScanOpen && (
    <div className="hm-gifts-sheet-overlay fd-mobile-sheet-backdrop hm-util-scan-sheet-overlay" onClick={closeFirstBillScanSheet} role="dialog" aria-modal="true" aria-label="Scan first utility bill">
      <button type="button" className="fd-mobile-sheet-scrim" onClick={event => { event.stopPropagation(); closeFirstBillScanSheet(); }} aria-label="Close scan bill" />
      <section
        className={`hm-gifts-action-sheet fd-mobile-sheet fd-mobile-sheet--food hm-util-scan-sheet${scanSheetDragging ? " is-dragging" : ""}`}
        style={{ transform: scanSheetDragOffset ? `translateY(${scanSheetDragOffset}px)` : undefined }}
        onClick={event => event.stopPropagation()}
        aria-label="Scan first utility bill"
      >
        <UtilitySheetTop
          handleRef={scanSheetHandleRef}
          onClose={closeFirstBillScanSheet}
          closeLabel="Close scan bill"
          handleProps={{
            onPointerDown: event => {
              if (event.pointerType === "mouse" && event.button !== 0) return;
              event.currentTarget.setPointerCapture?.(event.pointerId);
              beginScanSheetDrag(event.clientY);
            },
            onPointerMove: event => updateScanSheetDrag(event.clientY, event),
            onPointerUp: event => {
              event.currentTarget.releasePointerCapture?.(event.pointerId);
              finishScanSheetDrag();
            },
            onPointerCancel: event => {
              event.currentTarget.releasePointerCapture?.(event.pointerId);
              finishScanSheetDrag();
            },
          }}
        />
        <SheetLogoRow title="Scan bill" onClose={closeFirstBillScanSheet} closeLabel="Close scan bill" showClose={false} />
        <div className="hm-sheet-body">
          <div className="fd-scan-sheet-body">
            <section className="hm-panel hm-vault-scan-panel hm-vault-scan-panel--compact fd-food-scan-panel hm-util-bill-scan-panel">
              <div className="hm-vault-scan-note">
                Upload a clear photo of your most recent utility bill. HouseMind will prefill service and bill fields, then you confirm everything below.
              </div>

              {error && <div className="hm-inline-error">{error}</div>}

              <div className="hm-vault-scan-grid">
                <div className="hm-vault-scan-controls">
                  <label className="hm-vault-scan-consent">
                    <input type="checkbox" checked={scanConsent} onChange={e => setScanConsent(e.target.checked)} />
                    <span>I checked this bill has no full bank/card number visible.</span>
                  </label>

                  <div className={`fd-food-scan-drop${!scanConsent ? " is-disabled" : ""}`}>
                    <input ref={firstBillUploadRef} type="file" accept="image/*" disabled={!scanConsent} onChange={e => { handleFirstBillScan(e.target.files?.[0]); e.target.value = ""; }} />
                    <input ref={firstBillCameraRef} type="file" accept="image/*" capture="environment" disabled={!scanConsent} onChange={e => { handleFirstBillScan(e.target.files?.[0]); e.target.value = ""; }} />
                    <span className="hm-vault-scan-camera">📄</span>
                    <strong>Upload utility bill</strong>
                    <span>Photo of a bill — amount, due date, provider, account details, and usage will be shown for review.</span>
                    <div className="fd-food-scan-drop-actions">
                      <button type="button" disabled={!scanConsent || scanning || busy} onClick={() => openFirstBillPicker(firstBillUploadRef)}>Upload photo</button>
                      <button type="button" disabled={!scanConsent || scanning || busy} onClick={() => openFirstBillPicker(firstBillCameraRef)}>Use camera</button>
                    </div>
                  </div>
                </div>

                <div className="hm-vault-scan-review">
                  <h3>Detected bill details</h3>
                  {scanning ? (
                    <div className="hm-vault-empty hm-vault-scanning" style={{ marginTop: 0 }}>
                      <span className="hm-vault-scan-spinner" />
                      Reading your bill...
                    </div>
                  ) : (
                    <div className="hm-vault-empty" style={{ marginTop: 0 }}>
                      {scanReviewed ? "Scanned fields filled in. Confirm amount, due date, provider, and service type below." : "Upload a bill to review extracted details here."}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );

  if (presentation === "modal") {
    return (
      <>
        <CenteredModal title="Add a service" onClose={onClose}>
          {drawerContent}
        </CenteredModal>
        {firstBillScanSheet}
      </>
    );
  }

  return (
    <>
      <div className="hm-util-drawer-shell" role="dialog" aria-modal="true">
        <button type="button" className="hm-util-drawer-scrim" onClick={closeWithSlide} aria-label="Close add service" />
        <aside
          className={`hm-util-drawer${drawerDragging ? " is-dragging" : ""}`}
          style={{ transform: drawerDragOffset ? `translateY(${drawerDragOffset}px)` : undefined }}
        >
          {drawerContent}
        </aside>
      </div>
      {firstBillScanSheet}
    </>
  );
}

function UtilitiesAtGlance({ summary, intelligence, crossInsights, savedEfficiencyReport, onOpenSavings }) {
  const spike = intelligence.spikes[0];
  const monthly = Number(summary.monthly_total || 0);
  const annual = monthly * 12;
  const trackedCount = Number(summary.tracked_count || summary.active_count || 0);
  const savingsSignals = [
    spike,
    savedEfficiencyReport?.result_json?.priority_action ? savedEfficiencyReport : null,
  ].filter(Boolean).length;
  const spikePct = spike ? Number(spike.avg_spike_pct || spike.spike_pct || 0) : 0;
  return (
    <section className="hm-util-ai-overview" aria-label="Utilities intelligence summary">
      <div className="hm-util-overview-head">
        <span>Utilities intelligence</span>
        <strong>
          {spike
            ? `${spike.provider} needs attention`
            : crossInsights.length
              ? `${crossInsights.length} cross-module insight${crossInsights.length === 1 ? "" : "s"} ready`
              : "Bills look steady"}
        </strong>
        <p>
          {spike?.ai_summary || (spike
            ? `${spike.provider} is ${Math.round(spikePct)}% above its usual pattern.`
            : crossInsights.length
              ? "Review the cross-module card below for the linked action."
              : "Log each month to sharpen trends, savings estimates, and due-date reminders.")}
        </p>
      </div>
      <div className="hm-util-glance-grid">
        <div><span>Tracked monthly spend</span><strong>{fmtCompact$(monthly)}</strong><small>{trackedCount ? `${trackedCount} service${trackedCount === 1 ? "" : "s"} included` : "Add bills to track spend"}</small></div>
        <div className={spike ? "is-warn" : ""}><span>Spike detected</span><strong>{spike ? `+${Math.round(spikePct)}%` : "None"}</strong><small>{spike ? `${serviceLabel(spike.service_type).toLowerCase()} bill` : "No active spike"}</small></div>
        <button
          type="button"
          className="is-save hm-util-glance-button"
          onClick={onOpenSavings}
          aria-label={savingsSignals ? `View ${savingsSignals} savings signal explainers` : "View savings review"}
        >
          <span>Savings review</span>
          <strong>{savingsSignals ? `${savingsSignals} signal${savingsSignals === 1 ? "" : "s"}` : "Learning"}</strong>
          <small>{savingsSignals ? "Explain signals" : "More history needed"}<em>View</em></small>
        </button>
        <div><span>Estimated yearly spend</span><strong>{fmtCompact$(annual)}</strong><small>monthly spend x 12</small></div>
      </div>
    </section>
  );
}

function UtilitiesEfficiencySnapshot({ summary, intelligence, crossInsights, savedEfficiencyReport }) {
  const score = Math.max(40, Math.min(100, Number(summary.efficiency_score || 100)));
  const report = savedEfficiencyReport?.result_json || {};
  const grade = report.grade || (score >= 90 ? "A" : score >= 78 ? "B+" : score >= 68 ? "B" : score >= 58 ? "C" : "D");
  const stability = Math.max(35, Math.min(95, 100 - (intelligence.spikes.length * 18)));
  const rateFit = Math.max(35, Math.min(95, 82 - (crossInsights.some(i => i.insight_type === "ev_charging") ? 12 : 0)));
  const usageTrend = Math.max(35, Math.min(95, 84 - (intelligence.dueSoon.length * 5)));
  const noOverlaps = Math.max(25, Math.min(95, 92 - (crossInsights.filter(i => ["cable_streaming_overlap", "internet_subscription_overlap", "hoa_service_overlap"].includes(i.insight_type)).length * 25)));
  const ring = 138;
  const ringOffset = ring - (ring * score / 100);
  return (
    <section className="hm-util-efficiency-card" aria-label="Home efficiency score">
      <div className="hm-util-score-ring">
        <svg viewBox="0 0 52 52" aria-hidden="true">
          <circle cx="26" cy="26" r="22" />
          <circle cx="26" cy="26" r="22" style={{ strokeDasharray: ring, "--hm-ring-offset": ringOffset }} />
        </svg>
        <strong>{score}</strong>
      </div>
      <div className="hm-util-efficiency-copy">
        <strong>{grade} efficiency</strong>
        <p>{report.grade_explanation || (intelligence.spikes.length ? "Bill stability is being pulled down by current spikes. Fixing the top alert should improve the score." : "Your tracked utilities are steady. More bill history will make the score smarter.")}</p>
        <div className="hm-util-score-bars">
          <div><span>Bill stability</span><b style={{ "--score-w": `${stability}%` }} /><em>{stability}%</em></div>
          <div><span>Rate plan fit</span><b style={{ "--score-w": `${rateFit}%` }} /><em>{rateFit}%</em></div>
          <div><span>Usage trend</span><b style={{ "--score-w": `${usageTrend}%` }} /><em>{usageTrend}%</em></div>
          <div><span>No overlaps</span><b style={{ "--score-w": `${noOverlaps}%` }} /><em>{noOverlaps}%</em></div>
        </div>
      </div>
    </section>
  );
}

function UtilitiesSavingsReport({ intelligence, savedEfficiencyReport, sectionRef }) {
  const spike = intelligence.spikes[0];
  const report = savedEfficiencyReport?.result_json || {};
  const rows = [];
  if (spike) {
    const amount = Number(spike.amount || 0);
    const baseline = Number(spike.history_avg_amount || spike.avg_monthly_amount || spike.previous_amount || 0);
    const monthlySavings = Math.max(0, amount - baseline);
    rows.push({
      label: `${spike.provider} spike analysis`,
      detail: spike.ai_cause || spike.ai_summary || `${serviceLabel(spike.service_type)} bill is above average`,
      badge: "Spike",
      savings: monthlySavings ? `${fmtCompact$(monthlySavings)} above avg` : "",
      source: "Based on bill history",
    });
  }
  if (report.priority_action) {
    rows.push({
      label: "Annual efficiency priority",
      detail: report.priority_action,
      badge: report.grade ? `Grade ${report.grade}` : "Efficiency",
      savings: report.total_savings_potential || "",
      source: report.total_savings_potential ? "From saved AI report" : "Annual report",
    });
  }
  if (!rows.length) return null;
  return (
    <section ref={sectionRef} className="hm-util-savings-report" aria-label="AI savings report">
      <div className="hm-util-savings-head">
        <span>Savings opportunities</span>
        <em>{rows.length} item{rows.length === 1 ? "" : "s"}</em>
      </div>
      <div className="hm-util-savings-rows">
        {rows.slice(0, 4).map((row, index) => (
          <div key={`${row.label}-${index}`}>
            <span><strong>{row.label}</strong><small>{row.detail}</small></span>
            <em>{row.badge}</em>
            {(row.savings || row.source) && <b>{row.savings || row.source}<small>{row.savings ? row.source : ""}</small></b>}
          </div>
        ))}
      </div>
    </section>
  );
}

function UtilitiesAnnualReportSummary({ savedEfficiencyReport, reportResult }) {
  const report = savedEfficiencyReport?.result_json || reportResult || {};
  if (!savedEfficiencyReport && !reportResult) return null;
  return (
    <div className="hm-util-annual-summary">
      <div className="hm-util-annual-grade">{report.grade || "B+"}</div>
      <div>
        <strong>Annual efficiency report</strong>
        <p>{report.grade_explanation || report.summary || "Saved annual utility intelligence and home efficiency recommendations."}</p>
      </div>
    </div>
  );
}

function UtilitiesCrossModulePanel({ crossInsights, navigateToModule }) {
  const [learningOpen, setLearningOpen] = useState(false);
  const hasInsights = crossInsights.length > 0;
  return (
    <section className="hm-util-cross-panel" aria-label="Cross-module intelligence">
      <div className="hm-util-cross-head">
        <div>
          <div className="hm-util-section-label">Cross-module intelligence</div>
          {!hasInsights && <p>Connect saved records across HouseMind to unlock these utility insights.</p>}
        </div>
        {!hasInsights && (
          <button type="button" onClick={() => setLearningOpen(value => !value)}>
            {learningOpen ? "Hide" : "View"}
          </button>
        )}
      </div>
      {hasInsights ? crossInsights.slice(0, 4).map(insight => (
        <article key={`cross-${insight.id}`} className="hm-util-cross-card">
          <span className="hm-util-cross-icon">{insight.insight_type === "ev_charging" ? "EV" : ["cable_streaming_overlap", "internet_subscription_overlap"].includes(insight.insight_type) ? "TV" : "HM"}</span>
          <div>
            <strong>{utilityInsightTitle(insight)}</strong>
            <p>{utilityInsightMessage(insight)}</p>
          </div>
          <button type="button" onClick={() => navigateToModule(insight.action_module, insight.action_url)}>
            {insight.action_label || utilityInsightModuleLabel(insight.insight_type)}
          </button>
        </article>
      )) : learningOpen ? CROSS_MODULE_EMPTY_ROWS.map(row => (
        <article key={row.title} className="hm-util-cross-card hm-util-cross-card--empty">
          <span className="hm-util-cross-icon">{row.icon}</span>
          <div>
            <strong>{row.title}</strong>
            <p>{row.message}</p>
          </div>
          <em>{row.label}</em>
        </article>
      )) : null}
    </section>
  );
}

function UtilityTrendsPanel({ trendData, loading }) {
  const summary = trendData?.summary || {};
  const trends = trendData?.service_trends || [];
  const gaps = trendData?.outage_gaps || [];
  const correlations = trendData?.correlations || [];
  const topTrend = trends[0];
  const topGap = gaps[0];
  const topCorrelation = correlations[0];
  const trendLabel = topTrend?.amount_trend === "rising"
    ? "Rising"
    : topTrend?.amount_trend === "falling"
      ? "Falling"
      : "Stable";

  return (
    <section className="hm-util-trends" aria-label="Utility bill trend watch">
      <div className="hm-util-trends-head">
        <div>
          <div className="hm-util-section-label">Bill trend watch</div>
          <strong>
            {loading
              ? "Checking bill patterns..."
              : topTrend
                ? `${topTrend.provider} is ${trendLabel.toLowerCase()}`
                : "Add bill history to unlock trends"}
          </strong>
        </div>
        <span>Auto patterns</span>
      </div>
      <div className="hm-util-trends-metrics">
        <div><strong>{summary.rising_count || 0}</strong><span>rising</span></div>
        <div><strong>{summary.volatile_count || 0}</strong><span>volatile</span></div>
        <div><strong>{summary.outage_gap_count || 0}</strong><span>gaps</span></div>
        <div><strong>{summary.correlation_count || 0}</strong><span>links</span></div>
      </div>
      {topTrend ? (
        <div className="hm-util-trends-list">
          <article>
            <span className={`hm-util-trend-pill is-${topTrend.amount_trend}`}>{trendLabel}</span>
            <div>
              <strong>{topTrend.provider}</strong>
              <p>
                {Math.abs(Number(topTrend.amount_delta_pct || 0))}% {topTrend.amount_trend} vs previous bills.
                {topTrend.usage_trend !== "stable" ? ` Usage is ${topTrend.usage_trend}.` : ""}
              </p>
              <small>{topTrend.recommendation}</small>
            </div>
          </article>
          {topGap && (
            <article>
              <span className="hm-util-trend-pill is-gap">{topGap.gap_days}d</span>
              <div>
                <strong>{topGap.provider} gap detected</strong>
                <p>{topGap.label}</p>
                <small>{fmtDate(topGap.start_date)} to {fmtDate(topGap.end_date)}</small>
              </div>
            </article>
          )}
          {topCorrelation && (
            <article>
              <span className="hm-util-trend-pill is-link">Link</span>
              <div>
                <strong>{topCorrelation.provider}</strong>
                <p>{topCorrelation.message}</p>
                <small>{Math.abs(Number(topCorrelation.secondary_delta_pct || 0))}% related movement</small>
              </div>
            </article>
          )}
        </div>
      ) : (
        <p className="hm-util-trends-empty">
          Log at least a few bill entries per service and HouseMind will compare amount, usage, rate, volatility, and missing-bill gaps instantly.
        </p>
      )}
    </section>
  );
}

function UtilitiesAskBar() {
  const prompts = ["Why is my bill up?", "What is due soon?", "How can I save?"];

  function openUtilitiesAsk(question = "") {
    const clean = String(question || "").trim();
    const contextualQuestion = clean
      ? `${clean} Focus on utilities.`
      : "Ask about my utilities.";
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: contextualQuestion,
        context: {
          module: "utilities",
          module_label: "Utilities",
          surface: "Utilities",
        },
      },
    }));
  }

  return (
    <div className="fd-food-askbar hm-util-askbar">
      <input
        className="fd-food-askbar__input"
        placeholder="✦ Ask about your utilities..."
        onFocus={() => openUtilitiesAsk("")}
        readOnly
      />
      <div className="fd-food-askbar__chips">
        {prompts.map(prompt => (
          <button
            key={prompt}
            type="button"
            className="fd-food-askbar__chip"
            onClick={() => openUtilitiesAsk(prompt)}
          >
            {prompt}
          </button>
        ))}
      </div>
      <button type="button" className="fd-food-askbar__button" onClick={() => openUtilitiesAsk(prompts[0])}>
        ✦ Ask
      </button>
    </div>
  );
}

export default function UtilitiesPage() {
  const location = useLocation();
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesBillId, setEntriesBillId] = useState(null);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [openServiceMenuId, setOpenServiceMenuId] = useState(null);
  const [expandedUtilityId, setExpandedUtilityId] = useState(null);
  const [inlineReport, setInlineReport] = useState(null);
  const [inlineReportLoading, setInlineReportLoading] = useState(false);
  const [inlineReportError, setInlineReportError] = useState("");
  const [replaceInlineReportPrompt, setReplaceInlineReportPrompt] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmRemoveBill, setConfirmRemoveBill] = useState(null);
  const [undoDelete, setUndoDelete] = useState(null); // { bill, timerId }
  const [entryActionMessage, setEntryActionMessage] = useState("");
  const [crossInsights, setCrossInsights] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [savedEfficiencyReport, setSavedEfficiencyReport] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [efficiencyReportOpen, setEfficiencyReportOpen] = useState(false);
  const [replaceEfficiencyPrompt, setReplaceEfficiencyPrompt] = useState(false);
  const [billsSheetBillId, setBillsSheetBillId] = useState(null);
  const [savingsOpenSignal, setSavingsOpenSignal] = useState(0);
  const savingsSectionRef = useRef(null);
  const presentation = useFormPresentation();
  const detailsInSheet = presentation === "sheet";
  const showDeleting = useMinLoad(!!deletingId, 700);
  const showEntryAction = useMinLoad(Boolean(entryActionMessage), 700);
  const showGeneratingReport = useMinLoad(generatingReport, 700);
  const showGeneratingInlineReport = useMinLoad(inlineReportLoading, 700);
  const solarContext = useMemo(() => {
    const solarBills = bills.filter(bill => bill.service_type === "solar");
    const billingTypes = [...new Set(solarBills.map(bill => bill.solar_billing_type).filter(Boolean))].sort();
    return {
      hasSolar: solarBills.length > 0,
      billingTypes,
      hasNetMetering: billingTypes.includes("net_metering") || billingTypes.includes("loan_net_metering"),
    };
  }, [bills]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await utilitiesApi.list();
      const nextSummary = data.summary || {};
      setBills(data.bills || []);
      setSummary(nextSummary);
      setAlerts(data.alerts || []);
      const insights = await utilitiesApi.getCrossModuleInsights().catch(() => []);
      setCrossInsights(insights || []);
      const savedReport = await utilitiesApi.savedEfficiencyReport().catch(() => null);
      setSavedEfficiencyReport(savedReport);
      setReportResult(isSavedEfficiencyReportCurrent(savedReport, nextSummary) ? savedReport.result_json : null);
      setTrendLoading(true);
      const trends = await utilitiesApi.trends().catch(() => null);
      if (trends) setTrendData(trends);
      setError("");
    } catch (err) {
      setError(err.message || "Could not load bills.");
    } finally {
      setTrendLoading(false);
      setLoading(false);
    }
  }, []);

  const loadEntries = useCallback(async (billId) => {
    if (!billId) return;
    setEntriesBillId(billId);
    setEntriesLoading(true);
    setEntries([]);
    try {
      const data = await utilitiesApi.entries(billId);
      setEntries(data.entries || []);
      setError("");
    } catch (err) {
      setError(err.message || "Could not load bill history.");
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!bills.length) return;
    const params = new URLSearchParams(location.search || "");
    const action = params.get("action");
    const billId = Number(params.get("bill_id") || params.get("bill"));
    if (action !== "log" || !billId || !bills.some(bill => bill.id === billId)) return;
    setSelectedBillId(null);
    setExpandedUtilityId(null);
    setBillsSheetBillId(billId);
    window.history.replaceState(window.history.state, "", location.pathname);
  }, [bills, location.pathname, location.search]);

  useEffect(() => {
    if (billsSheetBillId) loadEntries(billsSheetBillId);
  }, [billsSheetBillId, loadEntries]);

  const intelligence = useMemo(() => {
    const dueSoon = bills.filter(b => {
      const days = daysUntil(b.due_date);
      return b.status !== "paid" && days != null && days >= 0 && days <= 7;
    });
    const spikes = bills.filter(b => Number(b.spike_pct || 0) >= 20 || Number(b.avg_spike_pct || 0) >= 20);
    const noDates = bills.filter(b => b.status !== "paid" && !b.due_date);
    const contracts = bills.filter(b => {
      const days = daysUntil(b.contract_end_date);
      return days != null && days >= 0 && days <= 45;
    });
    return { dueSoon, spikes, noDates, contracts };
  }, [bills]);

  const savingsSignal = useMemo(() => {
    if (intelligence.spikes.length > 0) return "Review bill changes";
    if (intelligence.noDates.length > 0) return "Add missing dates";
    if ((summary.active_count || 0) > 0) return "Looks steady";
    return "Start tracking";
  }, [intelligence, summary.active_count]);
  const savedEfficiencyReportCurrent = isSavedEfficiencyReportCurrent(savedEfficiencyReport, summary);
  const currentSavedEfficiencyReport = savedEfficiencyReportCurrent ? savedEfficiencyReport : null;
  const savedEfficiencyReportDisplay = savedEfficiencyReport && !savedEfficiencyReportCurrent
    ? {
        ...savedEfficiencyReport,
        needs_refresh: true,
        stale_reason: savedEfficiencyReport.stale_reason || "Utility totals changed since this report was generated.",
      }
    : savedEfficiencyReport;

  const reportEligible = useMemo(
    () => bills.some(bill => (bill.recent_entries || []).length >= 3) || bills.reduce((total, bill) => total + (bill.recent_entries || []).length, 0) >= 3,
    [bills],
  );
  const visibleAlerts = useMemo(
    () => {
      const crossKeys = new Set(crossInsights.map(insight => utilityCrossModuleKey(insight.insight_type, insight.message)));
      const seen = new Set();
      return alerts.filter(alert => {
        if (alert.alert_type === "bill_due") return false;
        const key = utilityCrossModuleKey(alert.alert_type, alert.message);
        if (crossKeys.has(key)) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    [alerts, crossInsights],
  );
  const trackedServiceCount = Number(summary.tracked_count || summary.active_count || 0);

  function mergeBill(result) {
    if (!result?.bill) return;
    setBills(current => current.some(item => item.id === result.bill.id)
      ? current.map(item => item.id === result.bill.id ? result.bill : item)
      : [result.bill, ...current]);
  }

  async function handleBillsSheetSaved(result, bill, nextDueDate) {
    if (result) mergeBill(result);
    if (bill?.id) setEntriesBillId(bill.id);
    if (result?.entries) {
      setEntries(result.entries);
    } else if (result?.entry) {
      setEntries(current => {
        const exists = current.some(item => item.id === result.entry.id);
        const next = exists
          ? current.map(item => item.id === result.entry.id ? result.entry : item)
          : [result.entry, ...current];
        return sortEntriesByDateDesc(next);
      });
    }
    if (bill?.id && nextDueDate) {
      try {
        const updated = await utilitiesApi.update(bill.id, { due_date: nextDueDate });
        setBills(current => current.map(item => item.id === bill.id ? updated : item));
      } catch (_) { /* best-effort */ }
    }
    load();
  }

  async function markEntryPaidForBill(bill, entry) {
    if (!bill || !entry) return;
    setEntryActionMessage("Marking bill paid...");
    setError("");
    try {
      const result = await utilitiesApi.markEntryPaid(bill.id, entry.id);
      mergeBill(result);
      if (billsSheetBillId === bill.id) {
        setEntries(result.entries || (result.entry ? [result.entry, ...entries] : entries));
      }
      await load();
    } catch (err) {
      setError(err.message || "Could not mark that bill paid.");
    } finally {
      setEntryActionMessage("");
    }
  }

  async function markLatestBillPaid(bill) {
    const entry = (bill?.recent_entries || []).find(item => item.status !== "paid");
    if (!entry) {
      setError("Log the current bill before marking it paid.");
      return;
    }
    await markEntryPaidForBill(bill, entry);
  }

  function commitRemoveBill(bill) {
    const id = bill.id;
    setDeletingId(id);
    setSelectedBillId(null);
    setExpandedUtilityId(current => current === id ? null : current);
    setBillsSheetBillId(current => current === id ? null : current);
    setOpenServiceMenuId(current => current === id ? null : current);
    utilitiesApi.remove(id)
      .then(() => load())
      .catch(err => {
        setBills(current => current.find(b => b.id === id) ? current : [bill, ...current]);
        setError(err.message || "Could not remove service.");
      })
      .finally(() => setDeletingId(null));
  }

  function removeBillWithUndo(bill) {
    if (undoDelete?.timerId) {
      clearTimeout(undoDelete.timerId);
      commitRemoveBill(undoDelete.bill);
    }
    setBills(current => current.filter(b => b.id !== bill.id));
    setSelectedBillId(null);
    setExpandedUtilityId(current => current === bill.id ? null : current);
    setBillsSheetBillId(current => current === bill.id ? null : current);
    setOpenServiceMenuId(current => current === bill.id ? null : current);
    const timerId = setTimeout(() => {
      setUndoDelete(null);
      commitRemoveBill(bill);
    }, 6000);
    setUndoDelete({ bill, timerId });
  }

  function undoRemoveBill() {
    if (!undoDelete) return;
    clearTimeout(undoDelete.timerId);
    setBills(current => current.find(b => b.id === undoDelete.bill.id) ? current : [undoDelete.bill, ...current]);
    setUndoDelete(null);
  }

  async function toggleAutoLogBill(billId, enabled) {
    setEntryActionMessage(enabled ? "Turning on auto-log..." : "Turning off auto-log...");
    setError("");
    try {
      const result = await utilitiesApi.toggleAutoLog(billId, enabled);
      setBills(current => current.map(b =>
        b.id === billId
          ? { ...b, auto_log_enabled: result.auto_log_enabled, auto_log_day: result.auto_log_day, autopay: result.autopay ?? b.autopay }
          : b
      ));
    } catch (err) {
      setError(err.message || "Could not update auto-log.");
    } finally {
      setEntryActionMessage("");
    }
  }

  async function pauseBill(billId, paused) {
    setError("");
    try {
      const result = await utilitiesApi.update(billId, { status: paused ? "paused" : "active" });
      setBills(current => current.map(b => b.id === billId ? { ...b, status: result.status ?? (paused ? "paused" : "active") } : b));
    } catch (err) {
      setError(err.message || "Could not update service status.");
    }
  }

  async function dismissAlert(alertId) {
    setError("");
    try {
      await utilitiesApi.dismissAlert(alertId);
      setAlerts(current => current.filter(alert => alert.id !== alertId));
    } catch (err) {
      setError(err.message || "Could not dismiss that utility alert.");
    }
  }

  function navigateToModule(module, url) {
    const target = url || {
      vault: "/app/home-vault",
      subscriptions: "/app/subscriptions",
      trips: "/app/events",
      vehicles: "/app/vehicles",
      utilities: "/app/utilities",
    }[module] || "/app/utilities";
    window.location.href = target;
  }

  function showSavedInlineReport(bill, reportType, report, action = null) {
    setInlineReport({
      billId: bill.id,
      type: reportType,
      result: report?.result_json || report || { message: action?.prompt },
      report,
    });
  }

  async function openUtilityReport(bill, reportType = "service_review", action = null, force = false) {
    setExpandedUtilityId(bill.id);
    setSelectedBillId(null);
    setBillsSheetBillId(null);
    setEntriesBillId(null);
    setEntries([]);
    setOpenServiceMenuId(null);
    setInlineReport(null);
    setInlineReportError("");
    setReplaceInlineReportPrompt(null);
    if (reportType === "history") {
      setExpandedUtilityId(null);
      setBillsSheetBillId(bill.id);
      return;
    }
    if (!force && persistedUtilityReportTypes([action]).includes(reportType)) {
      const existingReport = await getExistingUtilityReport(bill, reportType).catch(() => null);
      if (existingReport) {
        showSavedInlineReport(bill, reportType, existingReport, action);
        if (reportType !== "spike_analysis") {
          setReplaceInlineReportPrompt({ bill, reportType, action });
        }
        return;
      }
    }
    if (reportType === "spike_analysis") {
      setInlineReport({
        billId: bill.id,
        type: "spike_analysis",
        result: { cause: bill.ai_cause, summary: bill.ai_summary, fixes: bill.ai_fixes, call_provider: bill.ai_call_provider },
        report: null,
      });
      return;
    }
    setInlineReportLoading(true);
    try {
      if (reportType === "rate_plan") {
        const result = await utilitiesApi.optimize(bill.id, { force });
        if (result.needs_confirm) {
          showSavedInlineReport(bill, "rate_plan", result.report, action);
          setReplaceInlineReportPrompt({ bill, reportType, action });
          return;
        }
        setInlineReport({
          billId: bill.id,
          type: "rate_plan",
          result,
          report: { result_json: result, needs_refresh: false, created_at: new Date().toISOString() },
        });
        return;
      }
      if (reportType === "negotiation") {
        const result = await utilitiesApi.negotiation(bill.id, { location: bill.service_address || "", force });
        if (result.needs_confirm) {
          showSavedInlineReport(bill, "negotiation", result.report, action);
          setReplaceInlineReportPrompt({ bill, reportType, action });
          return;
        }
        setInlineReport({
          billId: bill.id,
          type: "negotiation",
          result,
          report: { result_json: result, needs_refresh: false, created_at: new Date().toISOString() },
        });
        return;
      }
      if (reportType === "provider_comparison") {
        const result = await utilitiesApi.providerComparison(bill.id, { location: bill.service_address || "", force });
        if (result.needs_confirm) {
          showSavedInlineReport(bill, "provider_comparison", result.report, action);
          setReplaceInlineReportPrompt({ bill, reportType, action });
          return;
        }
        setInlineReport({
          billId: bill.id,
          type: "provider_comparison",
          result,
          report: { result_json: result, needs_refresh: false, created_at: new Date().toISOString() },
        });
        return;
      }
      if (reportType === "efficiency") {
        const result = await utilitiesApi.efficiencyReport({ force });
        if (result.needs_confirm) {
          showSavedInlineReport(bill, "efficiency", result.report, action);
          setReplaceInlineReportPrompt({ bill, reportType, action });
          return;
        }
        setInlineReport({
          billId: bill.id,
          type: "efficiency",
          result,
          report: { result_json: result, needs_refresh: false, created_at: new Date().toISOString() },
        });
        return;
      }
      if (reportType.startsWith("solar_")) {
        const result = await utilitiesApi.serviceReview(bill.id, { mode: reportType, prompt: "", force });
        if (result.needs_confirm) {
          showSavedInlineReport(bill, reportType, result.report, action);
          setReplaceInlineReportPrompt({ bill, reportType, action });
          return;
        }
        setInlineReport({ billId: bill.id, type: reportType, result,
          report: { result_json: result, needs_refresh: false, created_at: new Date().toISOString() },
        });
        return;
      }
      if (reportType === "usage_review" || reportType === "efficiency_tips") {
        const result = await utilitiesApi.serviceReview(bill.id, {
          mode: reportType,
          prompt: action?.prompt || "",
          force,
        });
        if (result.needs_confirm) {
          showSavedInlineReport(bill, reportType, result.report, action);
          setReplaceInlineReportPrompt({ bill, reportType, action });
          return;
        }
        setInlineReport({
          billId: bill.id,
          type: reportType,
          result,
          report: { result_json: result, needs_refresh: false, created_at: new Date().toISOString() },
        });
        return;
      }
      setInlineReport({
        billId: bill.id,
        type: "service_review",
        result: { message: action?.prompt || utilityActionConfig(bill).actions?.[0]?.prompt || utilityActionConfig(bill).tertiaryPrompt || utilityActionConfig(bill).secondaryPrompt },
        report: { result_json: { message: action?.prompt }, needs_refresh: false, created_at: new Date().toISOString() },
      });
    } catch (err) {
      setInlineReportError(utilitiesAiErrorMessage(err, "Could not load this utility report."));
    } finally {
      setInlineReportLoading(false);
    }
  }

  function closeInlineDetails() {
    setExpandedUtilityId(null);
    setInlineReport(null);
    setInlineReportError("");
    setInlineReportLoading(false);
    setReplaceInlineReportPrompt(null);
  }

  async function handleGenerateReport(force = false) {
    if (!force && currentSavedEfficiencyReport && !currentSavedEfficiencyReport.needs_refresh) {
      setReplaceEfficiencyPrompt(true);
      return;
    }
    setGeneratingReport(true);
    setError("");
    try {
      const result = await utilitiesApi.efficiencyReport({ force });
      if (result.needs_confirm) {
        setSavedEfficiencyReport(result.report);
        setReplaceEfficiencyPrompt(true);
        return;
      }
      setReportResult(result);
      setSavedEfficiencyReport({
        result_json: result,
        source_context_json: { monthly_total: summary.monthly_total },
        needs_refresh: false,
        created_at: new Date().toISOString(),
      });
      setEfficiencyReportOpen(true);
    } catch (err) {
      setError(utilitiesAiErrorMessage(err, "Could not generate the efficiency report."));
    } finally {
      setGeneratingReport(false);
    }
  }

  return (
    <div className="hm-veh-page hm-util-page">
      <datalist id="hm-util-provider-suggestions">
        {COMMON_PROVIDER_SUGGESTIONS.map(provider => <option key={provider} value={provider} />)}
      </datalist>
      {showDeleting && <SavingNotice title="Removing" message="Removing utility service..." />}
      {showEntryAction && <SavingNotice title="Saving" message={entryActionMessage || "Updating bill entry..."} />}
      {showGeneratingReport && <SavingNotice title="Checking" message="Generating utility efficiency report..." />}
      {showGeneratingInlineReport && <SavingNotice title="Checking" message="Generating utility AI report..." />}
      {replaceEfficiencyPrompt && (
        <ReplaceReportBanner
          onCancel={() => setReplaceEfficiencyPrompt(false)}
          onReplace={() => {
            setReplaceEfficiencyPrompt(false);
            handleGenerateReport(true);
          }}
        />
      )}
      {replaceInlineReportPrompt && (
        <ReplaceReportBanner
          onCancel={() => setReplaceInlineReportPrompt(null)}
          onReplace={() => {
            const prompt = replaceInlineReportPrompt;
            setReplaceInlineReportPrompt(null);
            openUtilityReport(prompt.bill, prompt.reportType, prompt.action, true);
          }}
        />
      )}
      {confirmRemoveBill && (
        <ConfirmModal
          tag="⚠ Remove service"
          title={`Remove ${confirmRemoveBill.provider}?`}
          message="All bill history for this service will be permanently deleted."
          confirmLabel="Remove service"
          onCancel={() => setConfirmRemoveBill(null)}
          onConfirm={() => {
            const bill = confirmRemoveBill;
            setConfirmRemoveBill(null);
            removeBillWithUndo(bill);
          }}
          loading={deletingId === confirmRemoveBill.id}
        />
      )}
      {undoDelete && (
        <div className="hm-util-undo-toast" role="status" aria-live="polite">
          <span>{undoDelete.bill.provider || "Service"} removed</span>
          <button type="button" onClick={undoRemoveBill}>Undo</button>
        </div>
      )}
      <ModuleHeader
        className="hm-util-module-header"
        title="Utilities"
        subtitle="Track utility spend, due dates, fixed bills, and monthly changes."
        image={IMG_UTILITIES}
      >
        <div className="hm-module-stat">{fmt$(summary.monthly_total)}<span>/ mo</span></div>
        <div className="hm-module-stat">{summary.efficiency_score || 100}<span>score</span></div>
        {summary.spike_count > 0 && <div className="hm-module-stat" style={{ background: "rgba(245,166,35,0.35)" }}>{summary.spike_count}<span>spike</span></div>}
      </ModuleHeader>

      <section className="hm-util-mobile-hero" aria-label="Utilities summary">
        <div>
          <span>Utilities</span>
          <strong>{fmt$(summary.monthly_total, 0)} / month</strong>
          <small>{trackedServiceCount} tracked service{trackedServiceCount === 1 ? "" : "s"} · {summary.due_soon_count || 0} due soon</small>
        </div>
        <em>Score {summary.efficiency_score || 100} ✓</em>
      </section>

      {error && <div className="hm-inline-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="hm-util-toolbar">
        <div className="hm-sub-summary-bar">
          <div className="hm-sub-summary-card"><span>💳 Tracked monthly</span><strong>{fmt$(summary.monthly_total)}</strong></div>
          <div className="hm-sub-summary-card"><span>🔌 Tracked services</span><strong>{trackedServiceCount}</strong></div>
          <div className="hm-sub-summary-card"><span>⏰ Due soon</span><strong>{summary.due_soon_count || 0}</strong></div>
          <div className="hm-sub-summary-card"><span>💡 Home efficiency</span><strong>{summary.efficiency_score || 100}</strong></div>
        </div>
      </div>

      <div className="hm-sub-intel-strip">
        <div>
          <span>Utility intelligence</span>
          <strong>
            {crossInsights.length > 0
              ? `${crossInsights.length} insight${crossInsights.length > 1 ? "s" : ""} found`
              : savingsSignal}
          </strong>
        </div>
        <p>
          {intelligence.spikes.length > 0 && intelligence.spikes[0].ai_summary
            ? intelligence.spikes[0].ai_summary
            : summary.spike_count
              ? `${summary.spike_count} bill change${summary.spike_count === 1 ? "" : "s"} need review.`
              : crossInsights.length > 0
                ? `${utilityInsightMessage(crossInsights[0]).slice(0, 80)}...`
                : visibleAlerts.length
                  ? `${visibleAlerts.length} active insight${visibleAlerts.length === 1 ? "" : "s"} from HouseMind.`
                  : "Log each month to catch changes."}
        </p>
      </div>

      <UtilitiesAtGlance
        summary={summary}
        intelligence={intelligence}
        crossInsights={crossInsights}
        savedEfficiencyReport={currentSavedEfficiencyReport}
        onOpenSavings={() => {
          setSavingsOpenSignal(value => value + 1);
          window.setTimeout(() => {
            savingsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 80);
        }}
      />
      <MobileCollapseSection storageKey="hm_util_efficiency_open" title="💡 Efficiency snapshot" badge={`${summary.efficiency_score || 100}/100`}>
        <UtilitiesEfficiencySnapshot
          summary={summary}
          intelligence={intelligence}
          crossInsights={crossInsights}
          savedEfficiencyReport={currentSavedEfficiencyReport}
        />
      </MobileCollapseSection>
      <MobileCollapseSection
        storageKey="hm_util_savings_open"
        title="✦ AI savings report"
        badge={currentSavedEfficiencyReport ? "saved" : "on demand"}
        openSignal={savingsOpenSignal}
      >
        <UtilitiesSavingsReport
          intelligence={intelligence}
          savedEfficiencyReport={currentSavedEfficiencyReport}
          sectionRef={savingsSectionRef}
        />
      </MobileCollapseSection>
      <MobileCollapseSection storageKey="hm_util_cross_open" title="🔗 Cross-module intelligence" badge={`${crossInsights.length || 0}`}>
        <UtilitiesCrossModulePanel crossInsights={crossInsights} navigateToModule={navigateToModule} />
      </MobileCollapseSection>
      <MobileCollapseSection
        storageKey="hm_util_trends_open"
        title="📈 Utility trend watch"
        badge={`${trendData?.summary?.rising_count || 0} rising`}
      >
        <UtilityTrendsPanel trendData={trendData} loading={trendLoading} />
      </MobileCollapseSection>

      {visibleAlerts.length > 0 && (
        <div className="hm-util-alert-stack" aria-label="Utility intelligence alerts">
          {visibleAlerts.slice(0, 3).map(alert => (
            <article key={alert.id} className={`hm-util-alert-card is-${alert.severity || "info"}`}>
              <span>{alert.alert_type?.replace(/_/g, " ") || "Insight"}</span>
              <strong>{alert.title}</strong>
              <p>{utilityTripInsightMessage(alert.message)}</p>
              <div className="hm-util-alert-actions">
                {alert.action_url && (
                  <button type="button" onClick={() => navigateToModule("utilities", alert.action_url)}>
                    {alert.action_label || "Open"}
                  </button>
                )}
                <button type="button" onClick={() => dismissAlert(alert.id)}>Dismiss</button>
              </div>
            </article>
          ))}
        </div>
      )}

      {loading ? (
        <div className="hm-sub-loading">Loading bills...</div>
      ) : bills.length === 0 ? (
        <div className="hm-veh-empty-garage">
          <span>U</span>
          <h3>No utilities tracked yet</h3>
          <p>Add electric, water, gas, internet, solar, or trash to catch due dates and spikes.</p>
          <button type="button" className="hm-veh-add-btn" onClick={() => setAddOpen(true)}>+ Add utility</button>
        </div>
      ) : (
        <>
          <div className="hm-util-action-row hm-util-action-row--active">
            <button type="button" className="hm-util-add-service-btn" onClick={() => setAddOpen(true)}>+ Add service</button>
          </div>
          <div className="hm-util-active-panel">
            <div className="hm-util-active-head">
              <div>
                <span>Active utilities</span>
                <strong>{summary.active_count || bills.length} tracked service{(summary.active_count || bills.length) === 1 ? "" : "s"}</strong>
              </div>
              <small>{fmt$(summary.monthly_total, 0)} / mo</small>
            </div>
            <div className="hm-util-service-list">
              {bills.map(bill => (
                <div className="hm-util-service-item" key={bill.id}>
                  <UtilityServiceRow
                    bill={bill}
                    selected={selectedBillId === bill.id}
                    detailsOpen={expandedUtilityId === bill.id}
                    detailsInSheet={detailsInSheet}
                    menuOpen={openServiceMenuId === bill.id}
                    onMenu={() => setOpenServiceMenuId(openServiceMenuId === bill.id ? null : bill.id)}
                    onDetails={() => {
                      setExpandedUtilityId(expandedUtilityId === bill.id ? null : bill.id);
                      setInlineReport(null);
                      setInlineReportError("");
                      setInlineReportLoading(false);
                      setOpenServiceMenuId(null);
                      setSelectedBillId(null);
                    }}
                    onLogBill={() => {
                      setOpenServiceMenuId(null);
                      setBillsSheetBillId(bill.id);
                    }}
                    onEditService={() => {
                      closeInlineDetails();
                      setSelectedBillId(bill.id);
                      setOpenServiceMenuId(null);
                    }}
                    onSetAutopay={() => {
                      closeInlineDetails();
                      setSelectedBillId(bill.id);
                      setOpenServiceMenuId(null);
                    }}
                    onMarkPaid={() => {
                      setOpenServiceMenuId(null);
                      markLatestBillPaid(bill);
                    }}
                    onRemove={() => {
                      setOpenServiceMenuId(null);
                      closeInlineDetails();
                      setConfirmRemoveBill(bill);
                    }}
                    onPause={(billId, paused) => {
                      setOpenServiceMenuId(null);
                      pauseBill(billId, paused);
                    }}
                    onToggleAutoLog={(billId, enabled) => {
                      setOpenServiceMenuId(null);
                      toggleAutoLogBill(billId, enabled);
                    }}
                  />
                  {expandedUtilityId === bill.id && !detailsInSheet && (
                    <UtilityServiceInsightPanel
                      bill={bill}
                      activeReport={inlineReport?.billId === bill.id ? inlineReport : null}
                      reportLoading={inlineReportLoading && expandedUtilityId === bill.id}
                      reportError={inlineReport?.billId === bill.id || expandedUtilityId === bill.id ? inlineReportError : ""}
                      onRunAction={(action) => openUtilityReport(bill, action.report, action)}
                      onSaveRatePlan={async (ratePlan) => {
                        const updated = await utilitiesApi.update(bill.id, { rate_plan: ratePlan });
                        setBills(current => current.map(item => item.id === bill.id ? updated : item));
                      }}
                      solarContext={solarContext}
                      onEdit={() => { closeInlineDetails(); setSelectedBillId(bill.id); }}
                    />
                  )}
                  {expandedUtilityId === bill.id && detailsInSheet && (
                    <UtilityDetailsSheet
                      bill={bill}
                      activeReport={inlineReport?.billId === bill.id ? inlineReport : null}
                      reportLoading={inlineReportLoading && expandedUtilityId === bill.id}
                      reportError={inlineReport?.billId === bill.id || expandedUtilityId === bill.id ? inlineReportError : ""}
                      onRunAction={(action) => openUtilityReport(bill, action.report, action)}
                      onSaveRatePlan={async (ratePlan) => {
                        const updated = await utilitiesApi.update(bill.id, { rate_plan: ratePlan });
                        setBills(current => current.map(item => item.id === bill.id ? updated : item));
                      }}
                      solarContext={solarContext}
                      onClose={closeInlineDetails}
                    />
                  )}
                  {selectedBillId === bill.id && (
                    <UtilityDetailPanel
                      bill={bill}
                      onSavedService={(result) => {
                        mergeBill(result);
                        load();
                        setSelectedBillId(null);
                        setOpenServiceMenuId(null);
                      }}
                      onRemove={billId => {
                        const billToRemove = bills.find(item => item.id === billId) || bill;
                        setConfirmRemoveBill(billToRemove);
                      }}
                      onToggleAutoLog={toggleAutoLogBill}
                      onClose={() => {
                        setSelectedBillId(null);
                        setOpenServiceMenuId(null);
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          <button type="button" className="hm-util-add-another-link" onClick={() => setAddOpen(true)}>+ Add another service</button>
          <UtilitiesAnnualReportSummary savedEfficiencyReport={currentSavedEfficiencyReport} reportResult={reportResult} />
          {reportEligible && (
            <button type="button" className="hm-util-report-card" disabled={generatingReport} onClick={() => handleGenerateReport()}>
              <span className="hm-util-report-card__icon">📊</span>
              <span className="hm-util-report-card__text">
                <strong>{generatingReport ? "Generating report..." : savedEfficiencyReportDisplay ? (savedEfficiencyReportDisplay.needs_refresh ? "Refresh annual efficiency report" : "Re-run annual efficiency report") : "✦ Annual home efficiency report"}</strong>
                <small>AI analysis of home energy spend and savings</small>
              </span>
              {!generatingReport && <span className="hm-util-report-card__arrow">→</span>}
            </button>
          )}
          {savedEfficiencyReportDisplay ? (
            <SavedReportRow
              label="Annual home efficiency"
              report={savedEfficiencyReportDisplay}
              expanded={efficiencyReportOpen}
              onToggle={() => setEfficiencyReportOpen(!efficiencyReportOpen)}
            >
              {savedEfficiencyReportDisplay.needs_refresh ? (
                <div className="hm-util-empty-line">
                  This saved report needs a refresh before its savings estimates should be used.
                </div>
              ) : reportResult && <UtilityEfficiencyReportBody result={reportResult} />}
            </SavedReportRow>
          ) : reportResult && (
            <UtilityEfficiencyReportBody result={reportResult} />
          )}
        </>
      )}

      <div className="hm-sub-action-grid hm-util-action-insights">
        {intelligence.spikes.slice(0, 2).map(b => (
          <article
            key={`spike-${b.id}`}
            className="hm-sub-action-card hm-sub-action-warn hm-util-spike-card"
            onClick={() => {
              setBillsSheetBillId(b.id);
            }}
            >
            <span>Usage watch</span>
            <strong>{b.provider} up {b.avg_spike_pct || b.spike_pct}%</strong>
            <UtilitySpikeReportBody bill={b} result={{ cause: b.ai_cause, summary: b.ai_summary, fixes: b.ai_fixes, call_provider: b.ai_call_provider }} />
            {b.ai_seasonal === false && <div className="hm-util-spike-unusual">⚠ Unusual — not seasonal</div>}
          </article>
        ))}
        {intelligence.noDates.slice(0, 1).map(b => (
          <article key={`missing-${b.id}`} className="hm-sub-action-card">
            <span>Missing date</span>
            <strong>{b.provider}</strong>
            <small>Add a due date so HouseMind can remind you.</small>
          </article>
        ))}
        {intelligence.contracts?.slice(0, 1).map(b => (
          <article
            key={`contract-${b.id}`}
            className="hm-sub-action-card hm-util-contract-card"
            onClick={() => {
              setSelectedBillId(b.id);
            }}
          >
            <span>Contract ending · {b.contract_days_remaining || daysUntil(b.contract_end_date)}d</span>
            <strong>{b.provider}</strong>
            {b.contract_negotiation_tip ? (
              <p className="hm-util-spike-ai">{b.contract_negotiation_tip}</p>
            ) : (
              <small>Contract ends {fmtDate(b.contract_end_date)} — call now to negotiate renewal.</small>
            )}
            <div className="hm-util-spike-fix">📞 Call retention before it auto-renews</div>
          </article>
        ))}
      </div>

      <AddServiceDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={(result) => {
          mergeBill(result);
          setAddOpen(false);
          load();
        }}
      />
      {!addOpen && !selectedBillId && <UtilitiesAskBar />}

      {billsSheetBillId && (() => {
        const billsSheetBill = bills.find(b => b.id === billsSheetBillId);
        const billEntries = entriesBillId === billsSheetBill?.id ? entries : (billsSheetBill?.recent_entries || []);
        return billsSheetBill ? (
          <BillsSheet
            bill={billsSheetBill}
            entries={billEntries}
            entriesLoading={entriesLoading}
            onClose={() => {
              setBillsSheetBillId(null);
              setEntriesBillId(null);
              setEntries([]);
            }}
            onSaved={(result, nextDueDate) => handleBillsSheetSaved(result, billsSheetBill, nextDueDate)}
          />
        ) : null;
      })()}
    </div>
  );
}
